import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ManagementApi } from '../api/client';
import { Drawer } from '../components/Drawer';
import { RefreshActionButton } from '../components/RefreshActionButton';
import { createI18nNamespace, useI18n, useVisibleCopy } from '../i18n';
import {
  ConfigCard,
  ConfigDraftJsonDrawer,
  ConfigEditorSection,
  ConfigEditorShell,
  ConfigField,
  ConfigHelperText,
  ConfigKeyValueEditor,
  ConfigRepeatableRows,
  ConfigSegmentedControl,
  ConfigSelect,
  ConfigTextarea,
  ConfigTextInput,
  ConfigToggle,
  configValuesEqual,
  defaultDirtyDraftGuardMessage,
  useDirtyDraftGuard,
  type ConfigKeyValueEntry,
} from '../configEditor';
import { PropertyTable, ResourceHeader, StatusStrip, Tabs } from '../components/ResourceDetail';
import { REDACTED_VALUE, isSensitiveKey, redactedJson, redactSensitiveText } from '../lib/redaction';
import {
  buildSkillCreatePreview,
  buildSkillLifecyclePreview,
  buildLoadedSkillsModel,
  buildMCPPresetsModel,
  buildMCPServerDetailModel,
  buildMCPServersModel,
  buildSkillDetailModel,
  buildSkillLibraryModel,
  buildToolProxyModel,
  skillSpecFromStructuredDraft,
  type LoadedSkillRow,
  type LoadedSkillsModel,
  type MCPDefinitionDraftSpec,
  type MCPPresetsModel,
  type MCPServerDetailModel,
  type MCPServersModel,
  type SkillDetailModel,
  type SkillKeyValueRow,
  type SkillLifecycleKind,
  type SkillLibraryModel,
  type SkillMutationPreview,
  type SkillStringRow,
  type SkillStructuredDraftSpec,
  type ToolProxyModel,
} from './model';

type ResourceTab = 'overview' | 'json';
type MCPDetailTab = 'overview' | 'usage' | 'runtime' | 'json';
type SkillDetailDrawer = 'draft' | 'edit' | 'lifecycle' | 'result' | null;
type LoadState<Model, Sources> = {
  loading: boolean;
  model: Model;
  sources: Sources;
  errors: Record<string, string>;
};

type MCPDetailLoadState = LoadState<MCPServerDetailModel, MCPDetailSources> & {
  actionMessage: string;
};

type MCPDraftFormState = Omit<MCPDefinitionDraftSpec, 'args' | 'env'> & {
  argRows: SkillStringRow[];
  envRows: ConfigKeyValueEntry[];
  status: string;
};

type MCPImportDraftState = {
  presetName: string;
  projectId: string;
  secretBindings: Record<string, string>;
};

type MCPListDrawer =
  | { kind: 'create'; mode: 'native' | 'proxy' }
  | { kind: 'import' }
  | { kind: 'edit' | 'policy' | 'runtime'; serverId: string }
  | null;

type MCPDetailDrawer = 'draft' | 'lifecycle' | null;

type SkillLibrarySources = Parameters<typeof buildSkillLibraryModel>[0];
type SkillDetailSources = Parameters<typeof buildSkillDetailModel>[0];
type LoadedSkillsSources = Parameters<typeof buildLoadedSkillsModel>[0];
type SkillStructuredEditorValues = {
  manifestName: string;
  manifestDisplayName: string;
  manifestDescription: string;
  manifestStatus: string;
  tags: SkillStringRow[];
  body: string;
  scope: string;
  agents: SkillStringRow[];
  platforms: SkillStringRow[];
  entry: SkillKeyValueRow[];
  options: SkillKeyValueRow[];
  metadata: SkillKeyValueRow[];
  extraSpec: SkillKeyValueRow[];
};
type MCPSources = Parameters<typeof buildMCPServersModel>[0];
type MCPDetailSources = Parameters<typeof buildMCPServerDetailModel>[0];
type MCPPresetSources = Parameters<typeof buildMCPPresetsModel>[0];
type ToolProxySources = Parameters<typeof buildToolProxyModel>[0];

const rmCopy = createI18nNamespace('modules.resourceManagement');
const rmKey = (suffix: string) => rmCopy.key(suffix);

const resourceTabs: Array<{ id: ResourceTab; labelKey: string }> = [
  { id: 'overview', labelKey: 'modules.resourceManagement.tabs.overview' },
  { id: 'json', labelKey: 'modules.resourceManagement.tabs.redactedJson' },
];

const mcpDetailTabs: Array<{ id: MCPDetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'usage', label: 'Usage / consumers' },
  { id: 'runtime', label: 'Runtime evidence' },
  { id: 'json', label: 'View Config JSON' },
];

export function SkillLibraryPage() {
  const { locale, t } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>('overview');
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createPreview, setCreatePreview] = useState<SkillMutationPreview | null>(null);
  const [createConfirmed, setCreateConfirmed] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [state, setState] = useState<LoadState<SkillLibraryModel, SkillLibrarySources>>(() => {
    const sources: SkillLibrarySources = {};
    return { loading: true, sources, model: buildSkillLibraryModel(sources, { locale }), errors: {} };
  });

  const reload = useMemo(() => async () => {
    setState(previous => ({ ...previous, loading: true }));
    const errors: Record<string, string> = {};
    const [skills, conflicts] = await Promise.all([
      capture('skills', api.getSkills(), errors),
      capture('skill conflicts', api.getSkillConflicts(), errors),
    ]);
    const sources = { skills, conflicts, errors };
    setState({ loading: false, sources, model: buildSkillLibraryModel(sources, { locale }), errors });
  }, [api, locale]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!mounted) return;
      await reload();
    }
    void load();
    return () => { mounted = false; };
  }, [reload]);

  function previewCreateSkill() {
    setCreatePreview(buildSkillCreatePreview({ name: createName, displayName: createDisplayName, description: createDescription }));
    setCreateConfirmed(false);
    setCreateMessage('');
  }

  async function createSkill() {
    if (!createPreview || !createPreview.available || !createConfirmed) return;
    setCreateMessage('Creating Skill…');
    try {
      const result = await api.createSkill(createPreview.body);
      const record = asRecord(result);
      const id = text(record.id) || text(createPreview.body.name);
      setCreateMessage('Skill created. Opening detail…');
      await reload();
      window.location.hash = `#/skills/library/${encodeURIComponent(id)}`;
    } catch (error) {
      setCreateMessage(error instanceof Error ? error.message : String(error));
    }
  }

  function resetCreatePreview() {
    setCreatePreview(null);
    setCreateConfirmed(false);
    setCreateMessage('');
  }

  const { model } = state;
  return (
    <>
      <ResourcePageShell
        eyebrow="Skills"
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        status={model.status}
        loading={state.loading}
        notices={model.partialNotices}
        actions={<SkillActions onCreate={() => setCreateOpen(true)} onRefresh={reload} loading={state.loading} />}
        activeTab={activeTab}
        onTab={setActiveTab}
        rawJson={model.rawJson}
        showJsonTab={false}
      >
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{ui('Skill Library')}</h2>
              <p>{ui('Configured managed skill artifacts. Loaded Skills remains separate runtime evidence.')}</p>
            </div>
            <a className="btn btn-compact btn-subtle" href="#/skills/loaded">{t(rmKey('actions.loadedSkills'))}</a>
          </div>
          {model.emptyState ? <EmptyState {...model.emptyState} /> : (
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>{ui('Name')}</th><th>{ui('Status')}</th><th>{ui('Source')}</th><th>{ui('Usage evidence')}</th><th>{ui('Actions')}</th></tr></thead>
                <tbody>
                  {model.skills.map(skill => (
                    <tr key={skill.id}>
                      <td><strong>{skill.displayName}</strong><br /><small>{skill.name}</small></td>
                      <td>{ui(skill.status)}<br /><small>{ui(`published ${skill.publishedVersion} / current ${skill.currentVersion}`)}</small></td>
                      <td>{ui('Managed definition')}<br /><small>{ui(skill.evidenceState)}</small></td>
                      <td>{ui(skill.runtimeExposure)}</td>
                      <td><a className="btn btn-compact btn-subtle" href={skill.detailHref}>{t(rmKey('actions.openDetail'))}</a></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </ResourcePageShell>
      <Drawer title="Create Skill artifact" open={createOpen} onClose={() => setCreateOpen(false)} badge="structured">
        <SkillCreateDrawerContent
          name={createName}
          displayName={createDisplayName}
          description={createDescription}
          preview={createPreview}
          confirmed={createConfirmed}
          message={createMessage}
          onName={value => { setCreateName(value); resetCreatePreview(); }}
          onDisplayName={value => { setCreateDisplayName(value); resetCreatePreview(); }}
          onDescription={value => { setCreateDescription(value); resetCreatePreview(); }}
          onPreview={previewCreateSkill}
          onConfirm={setCreateConfirmed}
          onCreate={createSkill}
        />
      </Drawer>
    </>
  );
}

export function SkillDetailPage({ skillId, initialTab }: { skillId: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>(() => normalizeTab(initialTab));
  const [drawer, setDrawer] = useState<SkillDetailDrawer>(null);
  const [editorValues, setEditorValues] = useState<SkillStructuredEditorValues>(() => skillEditorValuesFromModel(buildSkillDetailModel({}, { locale })));
  const [editorBaseline, setEditorBaseline] = useState<SkillStructuredEditorValues>(() => skillEditorValuesFromModel(buildSkillDetailModel({}, { locale })));
  const [selectedPreview, setSelectedPreview] = useState<SkillMutationPreview | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [state, setState] = useState<LoadState<SkillDetailModel, SkillDetailSources>>(() => {
    const sources: SkillDetailSources = {};
    return { loading: true, sources, model: buildSkillDetailModel(sources, { locale }), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeTab(initialTab)), [initialTab]);

  const reload = useMemo(() => async (previous: SkillDetailSources = {}) => {
    setState(current => ({ ...current, loading: true }));
    const errors: Record<string, string> = {};
    const [skill, installations, conflicts, versions] = await Promise.all([
      capture('skill detail', api.getSkill(skillId), errors),
      capture('installations', api.getSkillInstallations(skillId), errors),
      capture('skill conflicts', api.getSkillConflicts(), errors),
      capture('versions', api.getSkillVersions(skillId), errors),
    ]);
    const sources = { ...previous, skill, installations, conflicts, versions, errors };
    const model = buildSkillDetailModel(sources, { locale });
    setState({ loading: false, sources, model, errors });
    return model;
  }, [api, locale, skillId]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      const model = await reload();
      if (!mounted) return;
      const nextEditorValues = skillEditorValuesFromModel(model);
      setEditorValues(nextEditorValues);
      setEditorBaseline(nextEditorValues);
      setSelectedPreview(null);
      setConfirmed(false);
      setActionMessage('');
    }
    void load();
    return () => { mounted = false; };
  }, [reload]);

  const editorDirty = !configValuesEqual(editorValues, editorBaseline);
  const dirtyGuard = useDirtyDraftGuard({ dirty: editorDirty, message: defaultDirtyDraftGuardMessage });

  function selectLifecycle(kind: SkillLifecycleKind, targetVersion?: number) {
    const preview = buildSkillLifecyclePreview(state.model, kind, {
      spec: kind === 'save_draft' ? skillSpecFromEditorValues(editorValues) : undefined,
      userEditedBody: editorValues.body !== editorBaseline.body,
      targetVersion,
    });
    setSelectedPreview(preview);
    setConfirmed(false);
    setActionMessage('');
  }

  function openLifecycleAction(kind?: SkillLifecycleKind, targetVersion?: number) {
    if (kind) {
      selectLifecycle(kind, targetVersion);
    } else {
      setSelectedPreview(null);
      setConfirmed(false);
      setActionMessage('');
    }
    setDrawer('lifecycle');
  }

  function openEditDrawer() {
    setSelectedPreview(null);
    setConfirmed(false);
    setActionMessage('');
    setDrawer('edit');
  }

  async function runSelectedPreview() {
    if (!selectedPreview || !selectedPreview.available) return;
    setActionMessage(`Applying ${selectedPreview.title}…`);
    try {
      let mutationResult: unknown;
      switch (selectedPreview.kind) {
        case 'save_draft':
          mutationResult = await api.saveSkillDraft(state.model.skillId, selectedPreview.body);
          setEditorBaseline(editorValues);
          break;
        case 'validate':
          mutationResult = await api.validateSkill(state.model.skillId);
          break;
        case 'preview':
          mutationResult = await api.getSkillPreview(state.model.skillId);
          break;
        case 'create_version':
          mutationResult = await api.createSkillVersion(state.model.skillId, selectedPreview.body);
          break;
        case 'publish':
          mutationResult = await api.publishSkill(state.model.skillId, numberValue(selectedPreview.body.version));
          break;
        case 'rollback':
          mutationResult = await api.rollbackSkill(state.model.skillId, numberValue(selectedPreview.body.version));
          break;
        case 'activate':
        case 'disable':
        case 'archive':
          mutationResult = await api.updateSkillStatus(state.model.skillId, selectedPreview.body);
          break;
        case 'delete':
          mutationResult = await api.deleteSkill(state.model.skillId);
          break;
      }
      const nextSources = {
        mutationResult,
        ...(selectedPreview.kind === 'validate' ? { validation: mutationResult } : {}),
        ...(selectedPreview.kind === 'preview' ? { preview: mutationResult } : {}),
      };
      setActionMessage(`${selectedPreview.title} completed. Live evidence refreshed.`);
      setDrawer('result');
      await reload(nextSources);
      setSelectedPreview(null);
      setConfirmed(false);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : String(error));
    }
  }

  const guardedSetDrawer = (nextDrawer: SkillDetailDrawer) => {
    if (nextDrawer === null && drawer !== null && editorDirty) {
      dirtyGuard.guardDrawerClose(() => setDrawer(null));
      return;
    }
    setDrawer(nextDrawer);
  };
  const selectDetailTab = (tab: ResourceTab) => {
    if (tab === activeTab) return;
    dirtyGuard.guardTabSwitch(tab, selectedTab => setActiveTab(normalizeTab(selectedTab)));
  };
  const updateEditorValues = (nextValues: SkillStructuredEditorValues) => {
    setEditorValues(nextValues);
    if (selectedPreview?.kind === 'save_draft') {
      setSelectedPreview(null);
      setConfirmed(false);
    }
  };
  const discardEditorChanges = () => {
    setEditorValues(editorBaseline);
    setSelectedPreview(null);
    setConfirmed(false);
    setActionMessage('');
  };

  const { model } = state;
  return (
    <>
      <ResourcePageShell
        eyebrow="Skill detail"
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        status={model.status}
        loading={state.loading}
        notices={model.partialNotices}
        actions={<SkillDetailActions onEdit={openEditDrawer} onLifecycle={() => openLifecycleAction()} onRefresh={async () => { await reload(); }} loading={state.loading} />}
        activeTab={activeTab}
        onTab={selectDetailTab}
        rawJson={model.rawJson}
      >
        <div className="content-grid">
          <section className="panel">
            <div className="panel-header"><div><h2>{ui('Artifact summary')}</h2><p>{ui(model.readonlyState)}</p></div></div>
            <PropertyTable rows={model.summaryRows} />
          </section>
          <section className="panel">
            <div className="panel-header"><div><h2>{ui('Definition vs runtime evidence')}</h2><p>{ui('Managed artifact changes stay here; Persona binding remains in Persona config.')}</p></div></div>
            <PropertyTable rows={[
              ['Managed definition', 'Draft, version, publish, rollback, archive, and delete actions open guarded drawers.'],
              ['Loaded Skills evidence', 'Runtime-loaded evidence is read-only and linked from usage rows.'],
              ['Persona binding', 'Bind, install, and unbind from Persona config, not from Skills.'],
            ]} />
          </section>
        </div>
        <SkillContentPanelsView model={model} />
        <section className="panel">
          <div className="panel-header"><div><h2>{ui('Version history')}</h2><p>{ui('Versions are immutable Skill artifact definitions; publishing changes the artifact pointer only.')}</p></div></div>
          <SkillVersionHistoryTable model={model} onSelect={openLifecycleAction} />
        </section>
        <section className="panel">
          <div className="panel-header"><div><h2>{ui('Read-only usage / consumers')}</h2><p>{ui('Navigate to Persona config or runtime evidence. Skill pages do not bind, install, or unbind Personas.')}</p></div></div>
          <SkillUsageEvidenceTable consumers={model.consumers} />
        </section>
      </ResourcePageShell>
      <Drawer title="Draft JSON / Diff" open={drawer === 'draft'} onClose={() => guardedSetDrawer(null)} badge="redacted">
        <SkillDraftJsonDrawerContent model={model} draft={skillSpecFromEditorValues(editorValues)} published={model.editor.publishedSpec} />
      </Drawer>
      <Drawer title="Edit Skill draft" open={drawer === 'edit'} onClose={() => guardedSetDrawer(null)} badge="structured">
        <div className="record-list">
          <SkillStructuredConfigEditor
            model={model}
            values={editorValues}
            dirty={editorDirty}
            onChange={updateEditorValues}
            onDiscard={discardEditorChanges}
            onDraftJson={() => setDrawer('draft')}
            onPreviewSave={() => selectLifecycle('save_draft')}
          />
          {selectedPreview?.kind === 'save_draft' ? <SkillMutationPreviewCard preview={selectedPreview} confirmed={confirmed} onConfirm={setConfirmed} onRun={runSelectedPreview} /> : null}
          {actionMessage ? <div className="notice"><strong>{ui('Action result:')}</strong> {ui(actionMessage)}</div> : null}
        </div>
      </Drawer>
      <Drawer title="Skill lifecycle action" open={drawer === 'lifecycle'} onClose={() => guardedSetDrawer(null)} badge="preview">
        <SkillLifecyclePanel
          model={model}
          preview={selectedPreview?.kind === 'save_draft' ? null : selectedPreview}
          confirmed={confirmed}
          actionMessage={actionMessage}
          onSelect={selectLifecycle}
          onConfirm={setConfirmed}
          onRun={runSelectedPreview}
        />
      </Drawer>
      <Drawer title="Skill action evidence" open={drawer === 'result'} onClose={() => guardedSetDrawer(null)} badge="redacted">
        <JsonBlock value={{ result: state.sources.mutationResult, validation: state.sources.validation, preview: state.sources.preview }} empty="No action result returned yet." />
      </Drawer>
    </>
  );
}

export function LoadedSkillsPage() {
  const { locale } = useI18n();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>('overview');
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState<LoadedSkillsModel, LoadedSkillsSources>>(() => {
    const sources: LoadedSkillsSources = {};
    return { loading: true, sources, model: buildLoadedSkillsModel(sources, { locale }), errors: {} };
  });

  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const personas = await capture('personas', api.getPersonas(), errors);
      const personaNames = extractPersonaNames(personas);
      const personaDetails = await Promise.all(personaNames.map(personaName => capture(`persona ${personaName}`, api.getPersona(personaName), errors)));
      if (!mounted) return;
      const sources = { personas, personaDetails: personaDetails.filter(Boolean), errors };
      setState({ loading: false, sources, model: buildLoadedSkillsModel(sources, { locale }), errors });
    }
    void load();
    return () => { mounted = false; };
  }, [api, locale, reloadToken]);

  return <LoadedSkillsView state={state} activeTab={activeTab} onTab={setActiveTab} onRefresh={() => setReloadToken(value => value + 1)} />;
}

export function LoadedSkillDetailPage({ personaId, skillName, initialTab }: { personaId: string; skillName: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>(() => normalizeTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState<LoadedSkillsModel, LoadedSkillsSources>>(() => {
    const sources: LoadedSkillsSources = {};
    return { loading: true, sources, model: buildLoadedSkillsModel(sources, { locale }), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeTab(initialTab)), [initialTab]);
  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const detail = await capture(`persona ${personaId}`, api.getPersona(personaId), errors);
      if (!mounted) return;
      const sources = { personas: { personas: [{ name: personaId }] }, personaDetails: detail ? [detail] : [], errors };
      setState({ loading: false, sources, model: buildLoadedSkillsModel(sources, { locale }), errors });
    }
    void load();
    return () => { mounted = false; };
  }, [api, locale, personaId, reloadToken]);

  const selected = state.model.loadedSkills.find(row => row.personaName === personaId && row.skillName === skillName);
  return (
    <ResourcePageShell
      eyebrow="Loaded skill"
      title={selected?.displayName ?? skillName}
      description="Runtime-backed skill evidence for a single persona. This does not infer platform/session exposure beyond live persona detail data."
      meta={[[ 'Persona', personaId ], [ 'Skill', skillName ], [ 'Evidence', 'Live persona detail' ]]}
      status={state.model.status}
      loading={state.loading}
      notices={state.model.partialNotices}
      actions={<SkillActions onRefresh={() => setReloadToken(value => value + 1)} loading={state.loading} />}
      activeTab={activeTab}
      onTab={setActiveTab}
      rawJson={state.model.rawJson}
      showJsonTab={false}
    >
      {selected ? <LoadedSkillCard row={selected} /> : <EmptyState title="Loaded skill not found." description="The live persona detail response did not include this skill." />}
    </ResourcePageShell>
  );
}

export function MCPServersPage() {
  const { t, locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>('overview');
  const [drawer, setDrawer] = useState<MCPListDrawer>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'native' | 'proxy'>('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterText, setFilterText] = useState('');
  const [createName, setCreateName] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createMessage, setCreateMessage] = useState('');
  const [creating, setCreating] = useState(false);
  const [selectedPresetName, setSelectedPresetName] = useState('github-mcp');
  const [installProjectId, setInstallProjectId] = useState('');
  const [secretBindings, setSecretBindings] = useState<Record<string, string>>({});
  const [installMessage, setInstallMessage] = useState('');
  const [installing, setInstalling] = useState(false);
  const [drawerDetail, setDrawerDetail] = useState<MCPDetailLoadState | null>(null);
  const [drawerDraftForm, setDrawerDraftForm] = useState<MCPDraftFormState>(() => draftFormFromSpec(buildMCPServerDetailModel({}, { locale }).editableSpec));
  const [drawerDraftBaseline, setDrawerDraftBaseline] = useState<MCPDefinitionDraftSpec>(() => buildMCPServerDetailModel({}, { locale }).editableSpec);
  const [drawerDraftJsonOpen, setDrawerDraftJsonOpen] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [importBaseline, setImportBaseline] = useState<MCPImportDraftState>(() => mcpImportDraftState('github-mcp', '', {}));
  const [state, setState] = useState<LoadState<MCPServersModel, MCPSources>>(() => {
    const sources: MCPSources = {};
    return { loading: true, sources, model: buildMCPServersModel(sources, { locale }), errors: {} };
  });

  const reload = useMemo(() => async () => {
    setState(previous => ({ ...previous, loading: true }));
    const errors: Record<string, string> = {};
    const [mcps, conflicts, personas, presets] = await Promise.all([
      capture('mcp servers', api.getMCPs(), errors),
      capture('mcp conflicts', api.getMCPConflicts(), errors),
      capture('personas', api.getPersonas(), errors),
      capture('mcp presets', api.getMCPPresets(), errors),
    ]);
    const personaNames = extractPersonaNames(personas);
    const personaDetails = await Promise.all(personaNames.map(name => capture(`persona ${name}`, api.getPersona(name), errors)));
    const sources = { mcps, conflicts, personas, personaDetails: personaDetails.filter(Boolean), presets, errors };
    setState({ loading: false, sources, model: buildMCPServersModel(sources, { locale }), errors });
  }, [api, locale]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!mounted) return;
      await reload();
    }
    void load();
    return () => { mounted = false; };
  }, [reload]);

  useEffect(() => {
    if (!drawer || !('serverId' in drawer)) {
      setDrawerDetail(null);
      return;
    }
    const serverId = drawer.serverId;
    let mounted = true;
    async function loadDetail() {
      const errors: Record<string, string> = {};
      const empty = buildMCPServerDetailModel({}, { locale });
      setDrawerDetail({ loading: true, sources: {}, model: empty, errors, actionMessage: '' });
      const [mcp, conflicts, audit] = await Promise.all([
        capture('mcp detail', api.getMCP(serverId), errors),
        capture('mcp conflicts', api.getMCPConflicts(), errors),
        capture('control-plane audit', api.getAudit({ resource_kind: 'artifact', resource_id: serverId, limit: '10' }), errors),
      ]);
      if (!mounted) return;
      const sources: MCPDetailSources = { mcp, conflicts, audit, personaDetails: state.sources.personaDetails, errors };
      const model = buildMCPServerDetailModel(sources, { locale });
      const nextDraftForm = draftFormFromSpec(model.editableSpec, model.artifactStatus);
      setDrawerDetail({ loading: false, sources, model, errors, actionMessage: '' });
      setDrawerDraftForm(nextDraftForm);
      setDrawerDraftBaseline(draftSpecFromForm(nextDraftForm));
      setDrawerDraftJsonOpen(false);
    }
    void loadDetail();
    return () => { mounted = false; };
  }, [api, drawer, locale, state.sources.personaDetails]);

  const { model } = state;
  const statusOptions = Array.from(new Set(model.servers.map(server => server.status))).filter(Boolean).sort();
  const filteredServers = model.servers.filter(server => {
    const query = filterText.trim().toLowerCase();
    const modeMatches = filterMode === 'all' || server.mode === filterMode;
    const statusMatches = filterStatus === 'all' || server.status === filterStatus;
    const textMatches = !query || [server.displayName, server.name, server.modeLabel, server.status, server.credentialPolicy, server.usedByLabel].join(' ').toLowerCase().includes(query);
    return modeMatches && statusMatches && textMatches;
  });
  const nativeServers = filteredServers.filter(server => server.mode === 'native');
  const proxyServers = filteredServers.filter(server => server.mode === 'proxy');
  const importPresets = getArray(asRecord(state.sources.presets).presets).map(item => asRecord(item));
  const selectedPreset = importPresets.find(preset => text(preset.name) === selectedPresetName) ?? importPresets[0];
  const selectedPresetKey = text(selectedPreset?.name);
  const selectedDrawerServer = drawer && 'serverId' in drawer ? model.servers.find(server => server.id === drawer.serverId || server.name === drawer.serverId) : undefined;
  const currentImportDraft = mcpImportDraftState(selectedPresetKey || selectedPresetName, installProjectId, secretBindings);
  const drawerDraftDirty = Boolean(drawer && 'serverId' in drawer && drawer.kind !== 'runtime' && !configValuesEqual(draftSpecFromForm(drawerDraftForm), drawerDraftBaseline));
  const importDirty = drawer?.kind === 'import' && !configValuesEqual(currentImportDraft, importBaseline);
  const mcpDrawerDirty = drawerDraftDirty || importDirty;
  const dirtyGuard = useDirtyDraftGuard({ dirty: () => mcpDrawerDirty, message: defaultDirtyDraftGuardMessage });

  const guardedSetDrawer = (nextDrawer: MCPListDrawer) => {
    const applyDrawer = () => {
      setDrawer(nextDrawer);
      if (!nextDrawer || !('serverId' in nextDrawer)) setDrawerDraftJsonOpen(false);
    };
    if (drawer && mcpDrawerDirty) {
      dirtyGuard.guardAction(applyDrawer);
      return;
    }
    applyDrawer();
  };

  const closeDrawer = () => guardedSetDrawer(null);

  const openCreateDrawer = (mode: 'native' | 'proxy') => {
    const nextDrawer = { kind: 'create' as const, mode };
    const open = () => {
      setCreateMessage('');
      setDrawer(nextDrawer);
      setDrawerDraftJsonOpen(false);
    };
    if (drawer && mcpDrawerDirty) dirtyGuard.guardAction(open);
    else open();
  };

  const openImportDrawer = () => {
    const nextPresetName = selectedPresetName || text(importPresets[0]?.name);
    const open = () => {
      setInstallMessage('');
      if (nextPresetName) setSelectedPresetName(nextPresetName);
      const nextDraft = mcpImportDraftState(nextPresetName, installProjectId, secretBindings);
      setImportBaseline(nextDraft);
      setDrawer({ kind: 'import' });
      setDrawerDraftJsonOpen(false);
    };
    if (drawer && mcpDrawerDirty) dirtyGuard.guardAction(open);
    else open();
  };

  const createMCP = async () => {
    setCreating(true);
    try {
      const result = await api.createMCP({ name: createName, display_name: createDisplayName, description: createDescription, actor: 'console' });
      const id = text(asRecord(result).id) || createName;
      setCreateMessage('Created managed MCP. Opening detail so the connection or proxy policy draft can be edited on demand.');
      await reload();
      if (id && typeof window !== 'undefined') window.location.hash = `#/tools/mcp-servers/${encodeURIComponent(id)}`;
    } catch (error) {
      setCreateMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setCreating(false);
    }
  };

  const installPreset = async () => {
    if (!selectedPresetKey) return;
    setInstalling(true);
    try {
      const result = await api.installMCPPreset(selectedPresetKey, { project_id: installProjectId, secret_bindings: secretBindings, actor: 'console' });
      setInstallMessage(`Installed preset into target project draft. ${redactedJson(result)}`);
      setImportBaseline(mcpImportDraftState(selectedPresetKey, installProjectId, secretBindings));
      await reload();
    } catch (error) {
      setInstallMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setInstalling(false);
    }
  };

  const saveDrawerDraft = async () => {
    if (!drawerDetail || !drawer || !('serverId' in drawer)) return;
    setDrawerSaving(true);
    try {
      const nextSpec = draftSpecFromForm(drawerDraftForm);
      await api.saveMCPDraft(drawer.serverId, {
        spec: nextSpec,
        base_revision: Number(asRecord(asRecord(drawerDetail.sources.mcp).draft).revision) || 0,
        actor: 'console',
      });
      setDrawerDraftBaseline(nextSpec);
      setDrawerDraftForm(draftFormFromSpec(nextSpec, drawerDraftForm.status));
      setDrawerDetail(previous => previous ? { ...previous, actionMessage: 'Draft saved. Raw headers are scrubbed and sensitive env placeholders are redacted; use secret_refs for credentials.' } : previous);
      await reload();
    } catch (error) {
      setDrawerDetail(previous => previous ? { ...previous, actionMessage: error instanceof Error ? error.message : String(error) } : previous);
    } finally {
      setDrawerSaving(false);
    }
  };

  const drawerTitle = drawer?.kind === 'create'
    ? drawer.mode === 'proxy' ? `${t(rmKey('actions.createProxy'))} MCP` : `${t(rmKey('actions.createNative'))} MCP`
    : drawer?.kind === 'import'
      ? 'Import MCP preset'
      : drawer?.kind === 'policy'
        ? 'Proxy prompt / permission / audit policy'
        : drawer?.kind === 'runtime'
          ? 'MCP runtime evidence'
          : 'Edit MCP configuration';

  return (
    <ResourcePageShell
      eyebrow="Tools/MCP"
      title={model.header.title}
      description={model.header.description}
      meta={model.header.meta}
      status={model.status}
      loading={state.loading}
      notices={model.partialNotices}
      actions={<ToolActions showNavigation={false} onCreateNative={() => openCreateDrawer('native')} onCreateProxy={() => openCreateDrawer('proxy')} onImportPreset={openImportDrawer} onRefresh={reload} loading={state.loading} />}
      activeTab={activeTab}
      onTab={tab => dirtyGuard.guardTabSwitch(tab, selectedTab => setActiveTab(normalizeTab(selectedTab)))}
      rawJson={model.rawJson}
      showJsonTab={false}
    >
      <section className="panel mcp-list-controls">
        <div className="panel-header">
          <div><h2>{ui('MCP inventory')}</h2><p>{ui('Default view stays list-first: filter definitions, inspect status, create/import from header drawers, and use row actions for details.')}</p></div>
        </div>
        <div className="mcp-filter-grid">
          <label className="field-label form-span">{ui('Filter MCPs')}<input value={filterText} onChange={event => setFilterText(event.target.value)} placeholder={ui('Name, mode, status, used by…')} /></label>
          <label className="field-label">{ui('Mode')}<select value={filterMode} onChange={event => setFilterMode(event.target.value as 'all' | 'native' | 'proxy')}><option value="all">{ui('All modes')}</option><option value="native">{ui('Native MCP')}</option><option value="proxy">{ui('cc-connect Proxy MCP')}</option></select></label>
          <label className="field-label">{ui('Status')}<select value={filterStatus} onChange={event => setFilterStatus(event.target.value)}><option value="all">{ui('All statuses')}</option>{statusOptions.map(status => <option value={status} key={status}>{status}</option>)}</select></label>
        </div>
      </section>
      <section className="panel mcp-mode-panel mcp-mode-native">
        <div className="panel-header"><div><h2>{ui('Native MCP')}</h2><p>{ui('Connection-first MCPs manage transport, command, URL, args, and secret refs from drawers or detail tabs.')}</p></div><span className="badge badge-counter">{nativeServers.length} / {model.nativeCount}</span></div>
        {nativeServers.length ? <MCPServerTable servers={nativeServers} evidenceLabel={t(rmKey('table.evidence'))} runtimeLabel={t(rmKey('table.runtimeExposure'))} onAction={guardedSetDrawer} /> : <EmptyState title="No Native MCP definitions match the filters." description="Create or import a non-proxy MCP to manage native connection settings." />}
      </section>
      <section className="panel mcp-mode-panel mcp-mode-proxy">
        <div className="panel-header"><div><h2>{ui('cc-connect Proxy MCP')}</h2><p>{ui('Prompt injection, permission policy, audit policy, and runtime evidence open on demand instead of occupying the list.')}</p></div><span className="badge badge-counter">{proxyServers.length} / {model.proxyCount}</span></div>
        {proxyServers.length ? <MCPServerTable servers={proxyServers} evidenceLabel={t(rmKey('table.evidence'))} runtimeLabel={t(rmKey('table.runtimeExposure'))} onAction={guardedSetDrawer} /> : <EmptyState title="No Proxy MCP definitions match the filters." description="Import a preset or enable proxy=true from an MCP edit drawer/detail tab." />}
      </section>
      <Drawer title={drawerTitle} open={Boolean(drawer)} onClose={closeDrawer} badge={drawer?.kind === 'runtime' ? 'evidence' : 'drawer'}>
        {drawer?.kind === 'create' ? (
          <MCPCreateDrawerContent mode={drawer.mode} name={createName} displayName={createDisplayName} description={createDescription} creating={creating} message={createMessage} onName={setCreateName} onDisplayName={setCreateDisplayName} onDescription={setCreateDescription} onCreate={() => void createMCP()} />
        ) : drawer?.kind === 'import' ? (
          <MCPPresetImportDrawerContent presets={importPresets} selectedPresetName={selectedPresetKey} selectedPreset={selectedPreset} projectId={installProjectId} secretBindings={secretBindings} installing={installing} message={installMessage} onPreset={setSelectedPresetName} onProjectId={setInstallProjectId} onSecretBinding={(key, value) => setSecretBindings(previous => ({ ...previous, [key]: value }))} onInstall={() => void installPreset()} />
        ) : drawer && 'serverId' in drawer ? (
          <MCPListDetailDrawerContent
            kind={drawer.kind}
            server={selectedDrawerServer}
            detail={drawerDetail}
            draftForm={drawerDraftForm}
            dirty={drawerDraftDirty}
            saving={drawerSaving}
            onDraftFormChange={setDrawerDraftForm}
            onDiscard={() => setDrawerDraftForm(draftFormFromSpec(drawerDraftBaseline, drawerDraftForm.status))}
            onDraftJson={() => setDrawerDraftJsonOpen(true)}
            onSaveDraft={() => void saveDrawerDraft()}
          />
        ) : null}
      </Drawer>
      <ConfigDraftJsonDrawer
        open={drawerDraftJsonOpen}
        onClose={() => setDrawerDraftJsonOpen(false)}
        title="MCP Draft JSON + diff"
        draft={draftSpecFromForm(drawerDraftForm)}
        published={drawerDetail?.model.publishedSpec ?? drawerDraftBaseline}
        redactionOptions={{ redactPromptContent: true }}
      />
    </ResourcePageShell>
  );
}

export function MCPServerDetailPage({ mcpId, initialTab }: { mcpId: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const ui = useVisibleCopy();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<MCPDetailTab>(() => normalizeMCPDetailTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [detailDrawer, setDetailDrawer] = useState<MCPDetailDrawer>(null);
  const [draftForm, setDraftForm] = useState<MCPDraftFormState>(() => draftFormFromSpec(buildMCPServerDetailModel({}, { locale }).editableSpec));
  const [draftBaseline, setDraftBaseline] = useState<MCPDefinitionDraftSpec>(() => buildMCPServerDetailModel({}, { locale }).editableSpec);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<MCPDetailLoadState>(() => {
    const sources: MCPDetailSources = {};
    return { loading: true, sources, model: buildMCPServerDetailModel(sources, { locale }), errors: {}, actionMessage: '' };
  });

  useEffect(() => setActiveTab(normalizeMCPDetailTab(initialTab)), [initialTab]);
  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const [mcp, conflicts, preview, versions, personas, audit] = await Promise.all([
        capture('mcp detail', api.getMCP(mcpId), errors),
        capture('mcp conflicts', api.getMCPConflicts(), errors),
        capture('mcp preview', api.getMCPPreview(mcpId), errors),
        capture('mcp versions', api.getMCPVersions(mcpId), errors),
        capture('personas', api.getPersonas(), errors),
        capture('control-plane audit', api.getAudit({ resource_kind: 'artifact', resource_id: mcpId, limit: '10' }), errors),
      ]);
      const personaNames = extractPersonaNames(personas);
      const personaDetails = await Promise.all(personaNames.map(name => capture(`persona ${name}`, api.getPersona(name), errors)));
      if (!mounted) return;
      const sources = { mcp, conflicts, preview, versions, personas, personaDetails: personaDetails.filter(Boolean), audit, errors };
      const model = buildMCPServerDetailModel(sources, { locale });
      const nextDraftForm = draftFormFromSpec(model.editableSpec, model.artifactStatus);
      setState(previous => ({ ...previous, loading: false, sources, model, errors }));
      setDraftForm(nextDraftForm);
      setDraftBaseline(draftSpecFromForm(nextDraftForm));
    }
    void load();
    return () => { mounted = false; };
  }, [api, locale, mcpId, reloadToken]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const parsed = draftSpecFromForm(draftForm);
      await api.saveMCPDraft(mcpId, {
        spec: parsed,
        base_revision: Number(asRecord(asRecord(state.sources.mcp).draft).revision) || 0,
        actor: 'console',
      });
      setDraftBaseline(parsed);
      setDraftForm(draftFormFromSpec(parsed, draftForm.status));
      setState(previous => ({ ...previous, actionMessage: 'Draft saved. Raw headers are scrubbed and sensitive env placeholders are redacted; secret values are never accepted here.' }));
    } catch (error) {
      setState(previous => ({ ...previous, actionMessage: error instanceof Error ? error.message : String(error) }));
    } finally {
      setSaving(false);
    }
  };

  const draftDirty = !configValuesEqual(draftSpecFromForm(draftForm), draftBaseline);
  const dirtyGuard = useDirtyDraftGuard({ dirty: draftDirty, message: defaultDirtyDraftGuardMessage });
  const selectMCPDetailTab = (tab: string) => {
    if (tab === activeTab) return;
    dirtyGuard.guardTabSwitch(tab, selectedTab => setActiveTab(normalizeMCPDetailTab(selectedTab)));
  };
  const discardMCPDraft = () => {
    setDraftForm(draftFormFromSpec(draftBaseline, draftForm.status || state.model.artifactStatus));
    setState(previous => ({ ...previous, actionMessage: '' }));
  };

  const { model } = state;
  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="MCP server detail"
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        actions={<div className="action-row"><button className="btn btn-subtle" type="button" onClick={() => setDetailDrawer('draft')}>{ui('Draft JSON + diff')}</button><button className="btn btn-subtle" type="button" onClick={() => setDetailDrawer('lifecycle')}>{ui('Lifecycle actions')}</button><ToolActions onRefresh={() => setReloadToken(value => value + 1)} loading={state.loading} /></div>}
      />
      <StatusStrip items={model.status} />
      <ResourceNotice loading={state.loading} notices={model.partialNotices} />
      {state.actionMessage ? <div className="notice"><strong>{ui('Result')}</strong> {ui(state.actionMessage)}</div> : null}
      <Tabs tabs={mcpDetailTabs} activeId={activeTab} onSelect={selectMCPDetailTab} />

      {activeTab === 'overview' ? (
        <MCPDetailOverview
          model={model}
          draftForm={draftForm}
          dirty={draftDirty}
          saving={saving}
          onDraftFormChange={setDraftForm}
          onDiscard={discardMCPDraft}
          onDraftJson={() => setDetailDrawer('draft')}
          onSaveDraft={() => void saveDraft()}
        />
      ) : null}
      {activeTab === 'usage' ? <MCPUsagePanel model={model} /> : null}
      {activeTab === 'runtime' ? <MCPRuntimeEvidencePanel model={model} /> : null}
      {activeTab === 'json' ? <section className="panel"><pre className="json-preview">{model.rawJson}</pre></section> : null}

      <ConfigDraftJsonDrawer
        open={detailDrawer === 'draft'}
        onClose={() => setDetailDrawer(null)}
        title="MCP Draft JSON + diff"
        draft={draftSpecFromForm(draftForm)}
        published={model.publishedSpec}
        redactionOptions={{ redactPromptContent: true }}
      />
      <Drawer title="MCP lifecycle actions" open={detailDrawer === 'lifecycle'} onClose={() => setDetailDrawer(null)} badge="preview">
        <MCPLifecyclePanel model={model} />
      </Drawer>
    </div>
  );
}

export function MCPPresetsPage({ presetName, initialTab }: { presetName?: string; initialTab?: string | null }) {
  const { t, locale } = useI18n();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>(() => normalizeTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [importOpen, setImportOpen] = useState(Boolean(presetName));
  const [selectedPresetName, setSelectedPresetName] = useState(presetName ?? 'github-mcp');
  const [installProjectId, setInstallProjectId] = useState('');
  const [secretBindings, setSecretBindings] = useState<Record<string, string>>({});
  const [installMessage, setInstallMessage] = useState('');
  const [installing, setInstalling] = useState(false);
  const [importBaseline, setImportBaseline] = useState<MCPImportDraftState>(() => mcpImportDraftState(presetName ?? 'github-mcp', '', {}));
  const [state, setState] = useState<LoadState<MCPPresetsModel, MCPPresetSources>>(() => {
    const sources: MCPPresetSources = {};
    return { loading: true, sources, model: buildMCPPresetsModel(sources, { locale }), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeTab(initialTab)), [initialTab]);
  useEffect(() => {
    if (presetName) {
      setSelectedPresetName(presetName);
      setImportBaseline(mcpImportDraftState(presetName, '', {}));
      setImportOpen(true);
    }
  }, [presetName]);
  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      const presets = await capture('mcp presets', api.getMCPPresets(), errors);
      if (!mounted) return;
      const sources = { presets, errors };
      setState({ loading: false, sources, model: buildMCPPresetsModel(sources, { locale }), errors });
    }
    void load();
    return () => { mounted = false; };
  }, [api, locale, reloadToken]);

  const visiblePresets = presetName ? state.model.presets.filter(preset => preset.name === presetName) : state.model.presets;
  const presetRecords = getArray(asRecord(state.sources.presets).presets).map(item => asRecord(item));
  const selectedPreset = presetRecords.find(item => text(item.name) === selectedPresetName) ?? presetRecords[0];
  const selectedPresetKey = text(selectedPreset?.name) || selectedPresetName;
  const currentImportDraft = mcpImportDraftState(selectedPresetKey, installProjectId, secretBindings);
  const importDirty = importOpen && !configValuesEqual(currentImportDraft, importBaseline);
  const dirtyGuard = useDirtyDraftGuard({ dirty: importDirty, message: defaultDirtyDraftGuardMessage });
  const openPresetImportDrawer = (nextPresetName = selectedPresetKey) => {
    const open = () => {
      if (nextPresetName) setSelectedPresetName(nextPresetName);
      setImportBaseline(mcpImportDraftState(nextPresetName, installProjectId, secretBindings));
      setImportOpen(true);
    };
    if (importOpen && importDirty) dirtyGuard.guardAction(open);
    else open();
  };
  const closePresetImportDrawer = () => dirtyGuard.guardDrawerClose(() => setImportOpen(false));
  const installPreset = async () => {
    if (!selectedPresetKey) return;
    setInstalling(true);
    try {
      const result = await api.installMCPPreset(selectedPresetKey, { project_id: installProjectId, secret_bindings: secretBindings, actor: 'console' });
      setInstallMessage(`Installed preset into target project draft. ${redactedJson(result)}`);
      setImportBaseline(mcpImportDraftState(selectedPresetKey, installProjectId, secretBindings));
    } catch (error) {
      setInstallMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setInstalling(false);
    }
  };
  return (
    <ResourcePageShell
      eyebrow={presetName ? 'MCP preset detail' : 'Tools/MCP'}
      title={presetName ? visiblePresets[0]?.displayName ?? presetName : state.model.header.title}
      description={state.model.header.description}
      meta={presetName ? [[t(rmKey('labels.preset')), presetName], [t(rmKey('labels.source')), '/api/v1/mcp_presets'], [t(rmKey('labels.actions')), 'Import wizard opens in drawer']] : state.model.header.meta}
      status={state.model.status}
      loading={state.loading}
      notices={state.model.partialNotices}
      actions={<ToolActions onImportPreset={() => openPresetImportDrawer()} onRefresh={() => setReloadToken(value => value + 1)} loading={state.loading} />}
      activeTab={activeTab}
      onTab={tab => dirtyGuard.guardTabSwitch(tab, selectedTab => setActiveTab(normalizeTab(selectedTab)))}
      rawJson={state.model.rawJson}
      showJsonTab={false}
    >
      <section className="panel">
        <div className="panel-header"><div><h2>{t(rmKey(presetName ? 'presets.summaryTitle' : 'presets.registryTitle'))}</h2><p>{t(rmKey('controls.installContractGapMessage'))}</p></div><button className="btn btn-subtle" type="button" onClick={() => openPresetImportDrawer()}>{t(rmKey('actions.openImportDrawer'))}</button></div>
        {visiblePresets.length ? <PresetTable presets={visiblePresets} onImport={preset => openPresetImportDrawer(preset)} /> : <EmptyState title={t(rmKey('presets.emptyTitle'))} description={t(rmKey('presets.missingDescription'))} />}
      </section>
      <Drawer title="Import MCP preset" open={importOpen} onClose={closePresetImportDrawer} badge="wizard">
        <MCPPresetImportDrawerContent presets={presetRecords} selectedPresetName={selectedPresetKey} selectedPreset={selectedPreset} projectId={installProjectId} secretBindings={secretBindings} installing={installing} message={installMessage} onPreset={setSelectedPresetName} onProjectId={setInstallProjectId} onSecretBinding={(key, value) => setSecretBindings(previous => ({ ...previous, [key]: value }))} onInstall={() => void installPreset()} />
      </Drawer>
    </ResourcePageShell>
  );
}

export function ToolProxyPage({ personaId, serverName, initialTab }: { personaId?: string; serverName?: string; initialTab?: string | null }) {
  const { locale } = useI18n();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<ResourceTab>(() => normalizeTab(initialTab));
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState<ToolProxyModel, ToolProxySources>>(() => {
    const sources: ToolProxySources = {};
    return { loading: true, sources, model: buildToolProxyModel(sources, { locale }), errors: {} };
  });

  useEffect(() => setActiveTab(normalizeTab(initialTab)), [initialTab]);
  useEffect(() => {
    let mounted = true;
    async function load() {
      setState(previous => ({ ...previous, loading: true }));
      const errors: Record<string, string> = {};
      if (personaId) {
        const detail = await capture(`persona ${personaId}`, api.getPersona(personaId), errors);
        if (!mounted) return;
        const sources = { personas: { personas: [{ name: personaId }] }, personaDetails: detail ? [detail] : [], errors };
        setState({ loading: false, sources, model: buildToolProxyModel(sources, { locale }), errors });
        return;
      }
      const personas = await capture('personas', api.getPersonas(), errors);
      const personaNames = extractPersonaNames(personas);
      const personaDetails = await Promise.all(personaNames.map(name => capture(`persona ${name}`, api.getPersona(name), errors)));
      if (!mounted) return;
      const sources = { personas, personaDetails: personaDetails.filter(Boolean), errors };
      setState({ loading: false, sources, model: buildToolProxyModel(sources, { locale }), errors });
    }
    void load();
    return () => { mounted = false; };
  }, [api, locale, personaId, reloadToken]);

  const selected = personaId && serverName ? state.model.proxyRows.find(row => row.personaName === personaId && row.serverName === serverName) : undefined;
  return (
    <ResourcePageShell
      eyebrow={personaId && serverName ? 'Tool proxy detail' : 'Tools/MCP'}
      title={selected?.serverName ?? state.model.header.title}
      description={personaId && serverName ? 'Runtime-backed MCP proxy detail for one persona/server pair.' : state.model.header.description}
      meta={personaId && serverName ? [['Persona', personaId], ['Server', serverName], ['Evidence', 'Live persona MCP snapshot']] : state.model.header.meta}
      status={state.model.status}
      loading={state.loading}
      notices={state.model.partialNotices}
      actions={<ToolActions onRefresh={() => setReloadToken(value => value + 1)} loading={state.loading} />}
      activeTab={activeTab}
      onTab={setActiveTab}
      rawJson={state.model.rawJson}
      showJsonTab={Boolean(personaId && serverName)}
    >
      {personaId && serverName
        ? selected ? <ToolProxyCard row={selected} /> : <EmptyState title="Tool proxy row not found." description="The live persona MCP snapshot did not include this server." />
        : <ToolProxyTables model={state.model} />}
    </ResourcePageShell>
  );
}

export function LoadedSkillsView({ state, activeTab, onTab, onRefresh }: { state: LoadState<LoadedSkillsModel, LoadedSkillsSources>; activeTab: ResourceTab; onTab: (tab: ResourceTab) => void; onRefresh?: () => void | Promise<void> }) {
  const { t } = useI18n();
  const ui = useVisibleCopy();
  const { model } = state;
  return (
    <ResourcePageShell
      eyebrow="Skills"
      title={model.header.title}
      description={model.header.description}
      meta={model.header.meta}
      status={model.status}
      loading={state.loading}
      notices={model.partialNotices}
      actions={<SkillActions onRefresh={onRefresh} loading={state.loading} />}
      activeTab={activeTab}
      onTab={onTab}
      rawJson={model.rawJson}
      showJsonTab={false}
    >
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Runtime-loaded skills')}</h2><p>{ui('Only live persona skill snapshots are shown as runtime evidence.')}</p></div></div>
        {model.emptyState ? <EmptyState {...model.emptyState} /> : (
          <table className="data-table">
            <thead><tr><th>{ui('Persona')}</th><th>{ui('Skill')}</th><th>{t(rmKey('table.evidence'))}</th><th>{ui('Source')}</th><th>{ui('Scope')}</th><th>{t(rmKey('labels.actions'))}</th></tr></thead>
            <tbody>{model.loadedSkills.map(row => (
              <tr key={`${row.personaName}:${row.skillName}`}>
                <td>{row.personaName}</td>
                <td><strong>{row.displayName}</strong><br /><small>{row.skillName}</small></td>
                <td>{ui(row.evidenceState)}</td>
                <td>{ui(row.sourceOrigin)}{row.managed ? ` ${ui('managed')}` : ''}</td>
                <td>{ui(row.scope)}</td>
                <td><div className="action-row row-actions"><a className="btn btn-compact btn-subtle" href={row.detailHref}>{t(rmKey('actions.openDetail'))}</a></div></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </section>
    </ResourcePageShell>
  );
}

function ResourcePageShell({
  eyebrow,
  title,
  description,
  meta,
  status,
  loading,
  notices,
  actions,
  activeTab,
  onTab,
  rawJson,
  showJsonTab = true,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  meta: Array<[string, string]>;
  status: Parameters<typeof StatusStrip>[0]['items'];
  loading: boolean;
  notices: string[];
  actions: React.ReactNode;
  activeTab: ResourceTab;
  onTab: (tab: ResourceTab) => void;
  rawJson: string;
  showJsonTab?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const visibleTabs = showJsonTab ? resourceTabs : resourceTabs.filter(tab => tab.id === 'overview');
  const effectiveActiveTab = showJsonTab ? activeTab : 'overview';

  return (
    <div className="page-stack">
      <ResourceHeader eyebrow={eyebrow} title={title} description={description} meta={meta} actions={actions} />
      <StatusStrip items={status} />
      <ResourceNotice loading={loading} notices={notices} />
      <Tabs tabs={visibleTabs.map(tab => ({ id: tab.id, label: t(tab.labelKey) }))} activeId={effectiveActiveTab} onSelect={tab => onTab(normalizeTab(tab))} />
      {effectiveActiveTab === 'overview' ? children : <section className="panel"><pre className="json-preview">{rawJson}</pre></section>}
    </div>
  );
}

function SkillCreateDrawerContent({
  name,
  displayName,
  description,
  preview,
  confirmed,
  message,
  onName,
  onDisplayName,
  onDescription,
  onPreview,
  onConfirm,
  onCreate,
}: {
  name: string;
  displayName: string;
  description: string;
  preview: SkillMutationPreview | null;
  confirmed: boolean;
  message: string;
  onName: (value: string) => void;
  onDisplayName: (value: string) => void;
  onDescription: (value: string) => void;
  onPreview: () => void;
  onConfirm: (checked: boolean) => void;
  onCreate: () => void;
}) {
  const ui = useVisibleCopy();

  return (
    <div className="record-list">
      <ConfigEditorSection title="Structured Skill create" description="Create initializes the manifest only. Draft body, entry/options, validation, versioning, publish, and rollback stay separated on the Skill detail lifecycle.">
        <div className="content-grid">
          <ConfigCard title="Manifest" description="These fields are sent to POST /skills after request preview and explicit confirmation.">
            <ConfigField id="skill-create-name" label="Name" required>
              <ConfigTextInput id="skill-create-name" value={name} onChange={onName} placeholder="deploy-helper" />
            </ConfigField>
            <ConfigField id="skill-create-display" label="Display name">
              <ConfigTextInput id="skill-create-display" value={displayName} onChange={onDisplayName} placeholder="Deploy Helper" />
            </ConfigField>
            <ConfigField id="skill-create-description" label="Description">
              <ConfigTextarea id="skill-create-description" rows={3} value={description} onChange={onDescription} placeholder="Managed Skill definition" />
            </ConfigField>
          </ConfigCard>
          <ConfigCard title="Initial status and boundary" description="Persona binding is not available from Skill create/edit pages.">
            <ConfigField label="Initial status">
              <ConfigSegmentedControl label="Initial Skill status" value="draft" onChange={() => undefined} options={skillStatusOptions.map(option => ({ ...option, disabled: option.value !== 'draft' }))} />
            </ConfigField>
            <PropertyTable rows={[
              ['Creates Persona binding', 'No'],
              ['Creates draft body', 'No; open detail editor after create'],
              ['Runtime exposure', 'Read-only evidence only'],
            ]} />
            <button className="btn" type="button" disabled={!name.trim()} onClick={onPreview}>{ui('Preview create request')}</button>
            {message ? <p className="notice">{ui(message)}</p> : null}
          </ConfigCard>
        </div>
      </ConfigEditorSection>
      <SkillMutationPreviewCard preview={preview} confirmed={confirmed} onConfirm={onConfirm} onRun={onCreate} />
    </div>
  );
}

export function SkillContentPanelsView({ model }: { model: SkillDetailModel }) {
  const ui = useVisibleCopy();

  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{ui('Skill content')}</h2><p>{ui('Markdown-first content view with structured scope/when fields.')}</p></div></div>
      <div className="content-grid">
        {model.contentPanels.map(panel => (
          <article className="record-card" key={`${panel.title}:${panel.source}`}>
            <strong>{ui(panel.title)}</strong>
            <p>{ui(panel.source)} · {ui('Scope')}: {ui(panel.scope)} · {ui(panel.when)}</p>
            {panel.body ? <MarkdownPreview markdown={panel.body} /> : <EmptyState title="No Skill body returned." description="Create or save a draft to add Markdown content." />}
          </article>
        ))}
      </div>
    </section>
  );
}

export function SkillStructuredConfigEditor({
  model,
  values,
  dirty,
  onChange,
  onDiscard,
  onDraftJson,
  onPreviewSave,
}: {
  model: SkillDetailModel;
  values: SkillStructuredEditorValues;
  dirty: boolean;
  onChange: (values: SkillStructuredEditorValues) => void;
  onDiscard: () => void;
  onDraftJson: () => void;
  onPreviewSave: () => void;
}) {
  const editable = model.editor.editable;
  const patch = (patchValues: Partial<SkillStructuredEditorValues>) => onChange({ ...values, ...patchValues });

  return (
    <ConfigEditorShell
      title="Structured Skill configuration"
      description={editable ? 'Edit Skill manifest context, entry/options, metadata, Markdown body, scope, and exposure filters with structured controls. Persona assignment remains read-only outside this editor.' : model.editor.contractGap}
      source={model.hasDraft ? 'draft' : model.publishedVersion ? 'published' : 'empty'}
      dirty={dirty}
      editing={editable}
      labels={{ saveDraft: 'Preview Save draft', draftJson: 'Draft JSON + diff', unsaved: 'Unsaved Skill draft' }}
      onSaveDraft={onPreviewSave}
      onDiscardChanges={onDiscard}
      onOpenDraftJson={onDraftJson}
    >
      <ConfigEditorSection title="Manifest, metadata, and status" description="Manifest fields are prefilled for detail review. Existing Skill manifest metadata is read-only until the backend exposes an update contract; status changes stay in lifecycle preview controls.">
        <div className="content-grid">
          <ConfigCard title="Manifest identity" description="Create flow owns initial manifest values; detail lifecycle keeps them visible without raw JSON editing.">
            <ConfigField id="skill-manifest-name" label="Name">
              <ConfigTextInput id="skill-manifest-name" value={values.manifestName} disabled onChange={() => undefined} />
            </ConfigField>
            <ConfigField id="skill-manifest-display" label="Display name">
              <ConfigTextInput id="skill-manifest-display" value={values.manifestDisplayName} disabled onChange={() => undefined} />
            </ConfigField>
            <ConfigField id="skill-manifest-description" label="Description">
              <ConfigTextarea id="skill-manifest-description" rows={3} value={values.manifestDescription} disabled onChange={() => undefined} />
            </ConfigField>
            <ConfigHelperText>Metadata edit endpoint gap: use Skill create for initial values; lifecycle status changes are preview-confirmed below.</ConfigHelperText>
          </ConfigCard>
          <ConfigCard title="Status and tags" description="Status is an enum control. Tags are preserved as manifest metadata evidence.">
            <ConfigField label="Current status">
              <ConfigSegmentedControl
                label="Skill status"
                value={values.manifestStatus || 'draft'}
                onChange={() => undefined}
                options={skillStatusOptions.map(option => ({ ...option, disabled: true }))}
              />
            </ConfigField>
            <ConfigField label="Tags" helperText="Read-only manifest metadata until backend manifest update support exists.">
              <SkillStringRows rows={values.tags} onChange={tags => patch({ tags })} addLabel="Add tag" itemLabel="Tag" disabled />
            </ConfigField>
          </ConfigCard>
        </div>
      </ConfigEditorSection>

      <ConfigEditorSection title="Entry and runtime filters" description="These fields become the Skill draft spec submitted to /skills/{id}/draft.">
        <div className="content-grid">
          <ConfigCard title="Entry / scope" description="Scope is validated by the backend as project or global.">
            <ConfigField id="skill-scope" label="Scope">
              <ConfigSelect
                id="skill-scope"
                value={values.scope || 'project'}
                disabled={!editable}
                onChange={scope => patch({ scope })}
                options={[{ value: 'project', label: 'project' }, { value: 'global', label: 'global' }]}
              />
            </ConfigField>
            <ConfigField label="Entry object" helperText="Optional structured entry metadata is saved as a draft object when present.">
              <SkillKeyValueRows entries={values.entry} onChange={entry => patch({ entry })} addLabel="Add entry field" disabled={!editable} />
            </ConfigField>
          </ConfigCard>
          <ConfigCard title="When filters" description="Repeatable rows keep stable identity while editing values.">
            <ConfigField label="Agents">
              <SkillStringRows rows={values.agents} onChange={agents => patch({ agents })} addLabel="Add agent" itemLabel="Agent" disabled={!editable} placeholder="wizard" />
            </ConfigField>
            <ConfigField label="Platforms">
              <SkillStringRows rows={values.platforms} onChange={platforms => patch({ platforms })} addLabel="Add platform" itemLabel="Platform" disabled={!editable} placeholder="linear" />
            </ConfigField>
          </ConfigCard>
        </div>
      </ConfigEditorSection>

      <ConfigEditorSection title="Options and metadata" description="Complex objects use key/value rows instead of raw JSON. Values accept JSON literals for arrays, objects, numbers, booleans, or null.">
        <div className="content-grid">
          <ConfigCard title="Options" description="Skill-owned options are preserved in the draft spec.">
            <SkillKeyValueRows entries={values.options} onChange={options => patch({ options })} addLabel="Add option" disabled={!editable} />
          </ConfigCard>
          <ConfigCard title="Metadata and extra spec fields" description="Unknown spec fields remain structured so the draft does not require raw JSON editing.">
            <ConfigField label="Metadata">
              <SkillKeyValueRows entries={values.metadata} onChange={metadata => patch({ metadata })} addLabel="Add metadata" disabled={!editable} />
            </ConfigField>
            <ConfigField label="Other spec fields">
              <SkillKeyValueRows entries={values.extraSpec} onChange={extraSpec => patch({ extraSpec })} addLabel="Add spec field" disabled={!editable} />
            </ConfigField>
          </ConfigCard>
        </div>
      </ConfigEditorSection>

      <ConfigEditorSection title="Markdown body" description="Markdown remains a structured textarea with a redacted preview; raw JSON is only available in the draft/diff drawer.">
        <div className="content-grid">
          <ConfigCard title="Body" description="Backend validation requires a non-empty body.">
            <ConfigField id="skill-body" label="Markdown body" required>
              <ConfigTextarea id="skill-body" rows={12} disabled={!editable} value={values.body} onChange={body => patch({ body })} />
            </ConfigField>
          </ConfigCard>
          <ConfigCard title="Preview" description="Sensitive token-like strings are redacted before display.">
            <MarkdownPreview markdown={redactSensitiveText(values.body)} />
          </ConfigCard>
        </div>
      </ConfigEditorSection>
    </ConfigEditorShell>
  );
}

const skillStatusOptions = [
  { value: 'draft', label: 'draft' },
  { value: 'active', label: 'active' },
  { value: 'disabled', label: 'disabled' },
  { value: 'archived', label: 'archived' },
];

let skillEditorRowSequence = 0;

function SkillStringRows({
  rows,
  onChange,
  addLabel,
  itemLabel,
  placeholder,
  disabled = false,
}: {
  rows: SkillStringRow[];
  onChange: (rows: SkillStringRow[]) => void;
  addLabel: string;
  itemLabel: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const ui = useVisibleCopy();

  if (disabled) {
    return rows.length ? (
      <div className="config-repeatable">
        {rows.map((row, index) => (
          <ConfigTextInput key={row.id} aria-label={`${itemLabel} ${index + 1}`} value={row.value} disabled onChange={() => undefined} placeholder={placeholder ?? itemLabel} />
        ))}
      </div>
    ) : <ConfigHelperText>{ui(`No ${itemLabel.toLowerCase()} rows configured.`)}</ConfigHelperText>;
  }
  return (
    <ConfigRepeatableRows
      items={rows}
      onChange={onChange}
      createItem={() => skillStringRow('')}
      addLabel={addLabel}
      getItemKey={row => row.id}
      renderItem={({ item, index, updateItem, removeItem }) => (
        <div className="config-key-value-row skill-string-row">
          <ConfigTextInput aria-label={`${itemLabel} ${index + 1}`} value={item.value} onChange={value => updateItem({ ...item, value })} placeholder={placeholder ?? itemLabel} />
          <button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove')}</button>
        </div>
      )}
    />
  );
}

function SkillKeyValueRows({ entries, onChange, addLabel, disabled = false }: { entries: SkillKeyValueRow[]; onChange: (entries: SkillKeyValueRow[]) => void; addLabel: string; disabled?: boolean }) {
  if (!disabled) return <ConfigKeyValueEditor entries={entries} onChange={nextEntries => onChange(configEntriesToSkillRows(nextEntries))} addLabel={addLabel} />;
  return entries.length ? (
    <div className="config-repeatable">
      {entries.map((entry, index) => (
        <div className="config-key-value-row" key={entry.id ?? index}>
          <ConfigTextInput aria-label={`Key ${index + 1}`} value={entry.key} disabled onChange={() => undefined} />
          <ConfigTextInput aria-label={`Value ${index + 1}`} value={entry.value} disabled onChange={() => undefined} />
        </div>
      ))}
    </div>
  ) : <ConfigHelperText>No fields configured.</ConfigHelperText>;
}

function skillEditorValuesFromModel(model: SkillDetailModel): SkillStructuredEditorValues {
  const manifest = model.editor.manifest;
  const structured = model.editor.structured;
  return {
    manifestName: manifest.name || model.name,
    manifestDisplayName: manifest.displayName || model.displayName,
    manifestDescription: manifest.description,
    manifestStatus: manifest.status || model.artifactStatus,
    tags: cloneSkillStringRows(manifest.tags),
    body: structured.body || model.editor.body,
    scope: structured.scope || model.editor.scope || 'project',
    agents: cloneSkillStringRows(structured.agents),
    platforms: cloneSkillStringRows(structured.platforms),
    entry: cloneSkillKeyValueRows(structured.entry),
    options: cloneSkillKeyValueRows(structured.options),
    metadata: cloneSkillKeyValueRows(structured.metadata),
    extraSpec: cloneSkillKeyValueRows(structured.extra),
  };
}

function skillSpecFromEditorValues(values: SkillStructuredEditorValues): Record<string, unknown> {
  return skillSpecFromStructuredDraft(skillDraftFromEditorValues(values));
}

function skillDraftFromEditorValues(values: SkillStructuredEditorValues): SkillStructuredDraftSpec {
  return {
    body: values.body,
    scope: values.scope,
    agents: cloneSkillStringRows(values.agents),
    platforms: cloneSkillStringRows(values.platforms),
    entry: cloneSkillKeyValueRows(values.entry),
    options: cloneSkillKeyValueRows(values.options),
    metadata: cloneSkillKeyValueRows(values.metadata),
    extra: cloneSkillKeyValueRows(values.extraSpec),
  };
}

function skillStringRow(value: string): SkillStringRow {
  return { id: nextSkillEditorRowId('string'), value };
}

function nextSkillEditorRowId(prefix: string) {
  skillEditorRowSequence += 1;
  return `skill-${prefix}-${skillEditorRowSequence}`;
}

function cloneSkillStringRows(rows: SkillStringRow[]): SkillStringRow[] {
  return rows.map(row => ({ id: row.id, value: row.value }));
}

function cloneSkillKeyValueRows(rows: SkillKeyValueRow[]): SkillKeyValueRow[] {
  return rows.map(row => ({ ...row }));
}

function configEntriesToSkillRows(entries: ConfigKeyValueEntry[]): SkillKeyValueRow[] {
  return entries.map(entry => ({ id: entry.id ?? nextSkillEditorRowId('kv'), key: entry.key, value: entry.value }));
}

export function SkillLifecyclePanel({
  model,
  preview,
  confirmed,
  actionMessage,
  onSelect,
  onConfirm,
  onRun,
}: {
  model: SkillDetailModel;
  preview: SkillMutationPreview | null;
  confirmed: boolean;
  actionMessage: string;
  onSelect: (kind: SkillLifecycleKind, targetVersion?: number) => void;
  onConfirm: (checked: boolean) => void;
  onRun: () => void;
}) {
  const ui = useVisibleCopy();

  const rollbackVersion = model.versions.find(version => version.version < model.publishedVersion)?.version;
  const statusTransitionOptions = skillStatusOptions.map(option => ({
    ...option,
    disabled: option.value === 'draft' || option.value === model.artifactStatus || model.artifactStatus === 'archived',
  }));
  const selectStatusTransition = (status: string) => {
    if (status === 'active') onSelect('activate');
    if (status === 'disabled') onSelect('disable');
    if (status === 'archived') onSelect('archive');
  };
  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{ui('Lifecycle controls')}</h2><p>{ui('Every mutation starts as a request preview; destructive actions require explicit confirmation.')}</p></div></div>
      <ConfigField label="Status transition" helperText="Status is previewed as a lifecycle PATCH request; draft status is backend-owned.">
        <ConfigSegmentedControl label="Skill lifecycle status" value={model.artifactStatus} onChange={selectStatusTransition} options={statusTransitionOptions} />
      </ConfigField>
      <div className="action-row">
        <button className="btn btn-compact" type="button" onClick={() => onSelect('validate')}>{ui('Validate')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect('preview')}>{ui('Preview')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect('create_version')}>{ui('Create version')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect('publish', model.latestVersion)}>{ui('Publish latest')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect('rollback', rollbackVersion)}>{ui('Rollback')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect(model.artifactStatus === 'disabled' || model.artifactStatus === 'draft' ? 'activate' : 'disable')}>{ui(model.artifactStatus === 'disabled' || model.artifactStatus === 'draft' ? 'Activate' : 'Disable')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect('archive')}>{ui('Archive')}</button>
        <button className="btn btn-compact" type="button" onClick={() => onSelect('delete')}>{ui('Delete')}</button>
      </div>
      <SkillMutationPreviewCard preview={preview} confirmed={confirmed} onConfirm={onConfirm} onRun={onRun} />
      {actionMessage ? <div className="notice"><strong>{ui('Action result:')}</strong> {ui(actionMessage)}</div> : null}
    </section>
  );
}

export function SkillMutationPreviewCard({ preview, confirmed, onConfirm, onRun }: { preview: SkillMutationPreview | null; confirmed: boolean; onConfirm: (checked: boolean) => void; onRun: () => void }) {
  const ui = useVisibleCopy();

  if (!preview) return <EmptyState title="No mutation preview selected." description="Choose a Skill lifecycle control to inspect the exact API request first." />;
  const requiresConfirmation = preview.destructive || preview.method !== 'GET';
  return (
    <article className="record-card">
      <strong>{ui(preview.title)}</strong>
      <p>{ui(preview.evidence)}</p>
      <RequestPreview preview={preview} />
      {preview.available ? (
        requiresConfirmation ? (
          <>
            <label className="confirm-row"><input type="checkbox" checked={confirmed} onChange={event => onConfirm(event.target.checked)} />{ui('I reviewed the request preview and understand this action.')}</label>
            <button className="btn btn-primary" type="button" disabled={!confirmed} onClick={onRun}>{ui('Apply')}</button>
          </>
        ) : <button className="btn btn-primary" type="button" onClick={onRun}>{ui('Run read-only action')}</button>
      ) : <div className="notice notice-warning"><strong>{ui('Backend/contract gap:')}</strong> {ui(preview.contractGap || preview.evidence)}</div>}
    </article>
  );
}

function RequestPreview({ preview }: { preview: SkillMutationPreview }) {
  const ui = useVisibleCopy();

  return (
    <div className="mutation-preview">
      <dl className="record-meta">
        <div><dt>{ui('Method')}</dt><dd>{preview.method}</dd></div>
        <div><dt>{ui('Endpoint')}</dt><dd>{preview.endpoint}</dd></div>
      </dl>
      <pre>{preview.bodyJson}</pre>
    </div>
  );
}

function SkillVersionHistoryTable({ model, onSelect }: { model: SkillDetailModel; onSelect: (kind: SkillLifecycleKind, targetVersion?: number) => void }) {
  const ui = useVisibleCopy();

  if (!model.versions.length) return <EmptyState title="No versions returned." description="Create a version after saving and validating a draft." />;
  return (
    <table className="data-table">
      <thead><tr><th>{ui('Version')}</th><th>{ui('Validation')}</th><th>{ui('Changelog')}</th><th>{ui('Actions')}</th></tr></thead>
      <tbody>{model.versions.map(version => (
        <tr key={version.version}>
          <td><strong>v{version.version}</strong><br /><small>{ui(version.isPublished ? 'published' : version.isCurrent ? 'current' : version.createdAt)}</small></td>
          <td>{ui(version.validationState)}<br /><small>{ui(version.createdBy)}</small></td>
          <td>{ui(version.changelog)}</td>
          <td className="action-row"><button className="btn btn-compact" type="button" onClick={() => onSelect('publish', version.version)}>{ui('Publish')}</button><button className="btn btn-compact" type="button" onClick={() => onSelect('rollback', version.version)}>{ui('Rollback')}</button></td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export function SkillUsageEvidenceTable({ consumers }: { consumers: SkillDetailModel['consumers'] }) {
  const ui = useVisibleCopy();

  if (!consumers.length) return <EmptyState title="No consumers returned." description="The reverse lookup found no Persona configs or runtime layers referencing this Skill." />;
  return (
    <table className="data-table">
      <thead><tr><th>{ui('Persona')}</th><th>{ui('State')}</th><th>{ui('Layers')}</th><th>{ui('Navigation')}</th></tr></thead>
      <tbody>{consumers.map(consumer => (
        <tr key={`${consumer.projectName}:${consumer.effectiveState}`}>
          <td><strong>{consumer.projectName}</strong><br /><small>{ui(consumer.runtimeLoaded ? 'runtime loaded evidence present' : 'runtime evidence not loaded')}</small></td>
          <td>{ui(consumer.effectiveState)}</td>
          <td>{ui(consumer.layers)}</td>
          <td><a href={consumer.configHref}>{ui('Persona config')}</a><br /><a href={consumer.runtimeHref}>{ui('Runtime evidence')}</a></td>
        </tr>
      ))}</tbody>
    </table>
  );
}

export function SkillDraftJsonDrawerContent({ model, draft, published }: { model: SkillDetailModel; draft?: Record<string, unknown>; published?: Record<string, unknown> }) {
  const ui = useVisibleCopy();
  const draftJson = draft ? redactedJson(draft) : model.draftJsonDrawer.draftJson;
  const publishedJson = published ? redactedJson(published) : model.draftJsonDrawer.publishedJson;
  const diffText = draft || published ? diffJsonLinesForDrawer(publishedJson, draftJson) : model.draftJsonDrawer.diffText;
  return (
    <div className="record-list">
      <p>{ui('Draft JSON is available only in this drawer. The primary Skill editing path is structured fields; this view shows the current unsaved draft projection and redacted diff.')}</p>
      <div><h3>{ui('Draft JSON drawer')}</h3><pre className="json-preview">{draftJson}</pre></div>
      <div><h3>{ui('Published JSON')}</h3><pre className="json-preview">{publishedJson}</pre></div>
      <div><h3>{ui('Draft vs published diff')}</h3><pre className="json-preview">{diffText}</pre></div>
    </div>
  );
}

function diffJsonLinesForDrawer(before: string, after: string) {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const max = Math.max(beforeLines.length, afterLines.length);
  const lines: string[] = [];
  for (let index = 0; index < max; index += 1) {
    const beforeLine = beforeLines[index];
    const afterLine = afterLines[index];
    if (beforeLine === afterLine) {
      if (beforeLine !== undefined) lines.push(` ${beforeLine}`);
      continue;
    }
    if (beforeLine !== undefined) lines.push(`-${beforeLine}`);
    if (afterLine !== undefined) lines.push(`+${afterLine}`);
  }
  return lines.join('\n');
}

function SkillActions({ loading = false, onCreate, onRefresh }: { loading?: boolean; onCreate?: () => void; onRefresh?: () => void | Promise<void> } = {}) {
  const { t } = useI18n();

  return (
    <div className="action-row">
      {onCreate ? <button className="btn btn-primary" type="button" onClick={onCreate}>{t(rmKey('actions.createSkill'))}</button> : null}
      <a className="btn btn-subtle" href="#/skills/library">{t(rmKey('actions.skillLibrary'))}</a>
      <a className="btn btn-subtle" href="#/skills/loaded">{t(rmKey('actions.loadedSkills'))}</a>
      {onRefresh ? <RefreshActionButton loading={loading} onClick={onRefresh} /> : null}
    </div>
  );
}

function SkillDetailActions({ loading = false, onEdit, onLifecycle, onRefresh }: { loading?: boolean; onEdit: () => void; onLifecycle: () => void; onRefresh?: () => void | Promise<void> }) {
  const { t } = useI18n();

  return (
    <div className="action-row">
      <button className="btn btn-primary" type="button" onClick={onEdit}>{t(rmKey('actions.editDraft'))}</button>
      <button className="btn btn-subtle" type="button" onClick={onLifecycle}>{t(rmKey('actions.lifecycleActions'))}</button>
      <a className="btn btn-subtle" href="#/skills/library">{t(rmKey('actions.skillLibrary'))}</a>
      <a className="btn btn-subtle" href="#/skills/loaded">{t(rmKey('actions.loadedSkills'))}</a>
      {onRefresh ? <RefreshActionButton loading={loading} onClick={onRefresh} /> : null}
    </div>
  );
}

function ToolActions({ loading = false, showNavigation = true, onCreateNative, onCreateProxy, onImportPreset, onRefresh }: { loading?: boolean; showNavigation?: boolean; onCreateNative?: () => void; onCreateProxy?: () => void; onImportPreset?: () => void; onRefresh?: () => void | Promise<void> } = {}) {
  const { t } = useI18n();

  return (
    <div className="action-row">
      {onCreateNative || onCreateProxy ? <button className="btn btn-primary" type="button" onClick={onCreateNative ?? onCreateProxy}>{onCreateNative ? t(rmKey('actions.createNative')) : t(rmKey('actions.createProxy'))}</button> : null}
      {onCreateNative && onCreateProxy ? <button className="btn btn-subtle" type="button" onClick={onCreateProxy}>{t(rmKey('actions.createProxy'))}</button> : null}
      {onImportPreset ? <button className="btn btn-subtle" type="button" onClick={onImportPreset}>{t(rmKey('actions.importPreset'))}</button> : null}
      {showNavigation ? <a className="btn btn-subtle" href="#/tools/mcp-servers">{t(rmKey('actions.mcpServers'))}</a> : null}
      {showNavigation ? <a className="btn btn-subtle" href="#/tools/mcp-presets">{t(rmKey('actions.mcpPresets'))}</a> : null}
      {showNavigation ? <a className="btn btn-subtle" href="#/tools/tool-proxy">{t(rmKey('actions.toolProxy'))}</a> : null}
      <span className="pill action-status">{t(rmKey('controls.personaBindingReadOnly')) || 'Persona usage read-only here'}</span>
      {onRefresh ? <RefreshActionButton loading={loading} onClick={onRefresh} /> : null}
    </div>
  );
}

function MCPCreateDrawerContent({
  mode,
  name,
  displayName,
  description,
  creating,
  message,
  onName,
  onDisplayName,
  onDescription,
  onCreate,
}: {
  mode: 'native' | 'proxy';
  name: string;
  displayName: string;
  description: string;
  creating: boolean;
  message: string;
  onName: (value: string) => void;
  onDisplayName: (value: string) => void;
  onDescription: (value: string) => void;
  onCreate: () => void;
}) {
  const ui = useVisibleCopy();
  const preview = {
    method: 'POST',
    endpoint: '/api/v1/mcps',
    body: { name, display_name: displayName, description, actor: 'console' },
  };
  return (
    <div className="mcp-drawer-stack">
      <section className="panel mcp-mode-panel mcp-mode-native">
        <div className="panel-header"><div><h2>{ui(mode === 'proxy' ? 'Create shell, then edit Proxy policy' : 'Create shell, then edit Native connection')}</h2><p>{ui('Create only the managed MCP artifact. Connection and proxy-owned fields stay in the edit drawer/detail tabs.')}</p></div></div>
        <div className="step-list">
          <article className="record-card"><strong>{ui('1. Define artifact')}</strong><p>{ui('Name, display name, and description create the MCP shell.')}</p></article>
          <article className="record-card"><strong>{ui('2. Edit draft')}</strong><p>{ui(mode === 'proxy' ? 'Open Prompt / policy to add prompt injection, permission, audit, secret refs, and runtime name.' : 'Open Edit connection to add transport, command, URL, args, and secret refs.')}</p></article>
          <article className="record-card"><strong>{ui('3. Review detail tabs')}</strong><p>{ui('Use details for View Config JSON, draft diff, usage, and runtime evidence.')}</p></article>
        </div>
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('MCP shell fields')}</h2><p>{ui('No Persona assignment happens here; the list will show read-only usage after configs reference it.')}</p></div></div>
        <div className="form-grid">
          <label className="field-label">{ui('Name')}<input value={name} onChange={event => onName(event.target.value)} placeholder="github-mcp" /></label>
          <label className="field-label">{ui('Display name')}<input value={displayName} onChange={event => onDisplayName(event.target.value)} placeholder="GitHub MCP" /></label>
          <label className="field-label form-span">{ui('Description')}<input value={description} onChange={event => onDescription(event.target.value)} placeholder={ui('Managed MCP definition')} /></label>
        </div>
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Create request preview')}</h2><p>{ui('Exact request preview is drawer-only; the default list never renders request previews.')}</p></div></div>
        <PropertyTable rows={[[ 'Method', preview.method ], [ 'Endpoint', preview.endpoint ], [ 'Actor', 'console' ], [ 'Next drawer', mode === 'proxy' ? 'Prompt / policy' : 'Edit connection' ]]} />
        <pre className="json-preview">{redactedJson(preview.body, { redactPromptContent: true })}</pre>
        <div className="action-row"><button className="btn btn-primary" type="button" disabled={creating || !name.trim()} onClick={onCreate}>{ui(creating ? 'Creating…' : 'Create MCP shell')}</button></div>
        {message ? <div className="notice"><strong>{ui('Create result')}</strong> {ui(message)}</div> : null}
      </section>
    </div>
  );
}

export function MCPPresetImportDrawerContent({
  presets,
  selectedPresetName,
  selectedPreset,
  projectId,
  secretBindings,
  installing,
  message,
  onPreset,
  onProjectId,
  onSecretBinding,
  onInstall,
}: {
  presets: Record<string, unknown>[];
  selectedPresetName: string;
  selectedPreset: Record<string, unknown> | undefined;
  projectId: string;
  secretBindings: Record<string, string>;
  installing: boolean;
  message: string;
  onPreset: (value: string) => void;
  onProjectId: (value: string) => void;
  onSecretBinding: (key: string, value: string) => void;
  onInstall: () => void;
}) {
  const ui = useVisibleCopy();
  const [confirmed, setConfirmed] = useState(false);
  const requiredSecrets = getArray(selectedPreset?.required_secrets).map(item => asRecord(item));
  const presetName = selectedPresetName || text(selectedPreset?.name);
  useEffect(() => {
    setConfirmed(false);
  }, [presetName, projectId, secretBindings]);
  const preview = {
    method: 'POST',
    endpoint: `/api/v1/mcp_presets/${presetName || '{name}'}/install`,
    body: { project_id: projectId, secret_bindings: secretBindings, actor: 'console' },
  };
  return (
    <div className="mcp-drawer-stack">
      <section className="panel mcp-mode-panel mcp-mode-proxy">
        <div className="panel-header"><div><h2>{ui('Preset import wizard')}</h2><p>{ui('GitHub-style presets remain fast, but install runs through steps, preview, and confirmation inside this drawer.')}</p></div></div>
        <div className="step-list">
          <article className="record-card"><strong>{ui('1. Choose preset')}</strong><p>{ui('Select a backend registry preset; the default list only links to this wizard.')}</p></article>
          <article className="record-card"><strong>{ui('2. Bind secret refs')}</strong><p>{ui('Enter registry keys only. Secret values are not accepted or displayed.')}</p></article>
          <article className="record-card"><strong>{ui('3. Target project draft')}</strong><p>{ui('The backend install contract requires project_id and appends an artifact ref to that draft for later review/publish.')}</p></article>
        </div>
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Preset and bindings')}</h2><p>{text(selectedPreset?.description) || ui('No preset description returned.')}</p></div></div>
        <div className="form-grid">
          <ConfigField label="Preset">
            <ConfigSelect value={presetName} onChange={onPreset} options={presets.map(preset => ({ value: text(preset.name), label: text(preset.display_name) || text(preset.name) }))} />
          </ConfigField>
          <ConfigField label="Target project draft" required>
            <ConfigTextInput value={projectId} onChange={onProjectId} placeholder="wizard or project manifest id" />
          </ConfigField>
          <ConfigField label="Transport override" helperText="Preset transport is applied by backend install; edit transport after import in the MCP detail drawer.">
            <ConfigTextInput value={text(selectedPreset?.type) || 'http'} disabled onChange={() => undefined} />
          </ConfigField>
          <ConfigField label="Runtime name override" helperText="Runtime name defaults to the preset key. Backend override support is a contract gap.">
            <ConfigTextInput value={presetName} disabled onChange={() => undefined} />
          </ConfigField>
          {requiredSecrets.map(secret => {
            const key = text(secret.key);
            return (
              <ConfigField label={`Secret registry key for ${key || text(secret.target)}`} helperText={text(secret.description) || text(secret.target)} key={key || text(secret.target)}>
                <ConfigTextInput value={secretBindings[key] ?? ''} onChange={value => onSecretBinding(key, value)} placeholder="github.token.prod" />
              </ConfigField>
            );
          })}
        </div>
        <div className="confirm-row">{ui('Secret values are not accepted here; only existing registry refs are submitted.')}</div>
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Install request preview')}</h2><p>{ui('Preview and confirmation stay inside the drawer, not on the default MCP list.')}</p></div></div>
        <PropertyTable rows={[[ 'Method', preview.method ], [ 'Endpoint', preview.endpoint ], [ 'Creates', 'Proxy MCP artifact + target project draft ref' ], [ 'Publishes Persona config', 'No' ]]} />
        <pre className="json-preview">{redactedJson(preview.body, { redactPromptContent: true })}</pre>
        <label className="confirm-row"><input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />{ui('I reviewed the preset import preview and target project draft impact.')}</label>
        <div className="action-row"><button className="btn btn-primary" type="button" disabled={installing || !projectId.trim() || !presetName || !confirmed} onClick={onInstall}>{ui(installing ? 'Installing…' : 'Install preset')}</button></div>
        {message ? <div className="notice"><strong>{ui('Install result')}</strong> {ui(message)}</div> : null}
      </section>
    </div>
  );
}

function MCPListDetailDrawerContent({
  kind,
  server,
  detail,
  draftForm,
  dirty,
  saving,
  onDraftFormChange,
  onDiscard,
  onDraftJson,
  onSaveDraft,
}: {
  kind: 'edit' | 'policy' | 'runtime';
  server: MCPServersModel['servers'][number] | undefined;
  detail: MCPDetailLoadState | null;
  draftForm: MCPDraftFormState;
  dirty: boolean;
  saving: boolean;
  onDraftFormChange: (value: MCPDraftFormState) => void;
  onDiscard: () => void;
  onDraftJson: () => void;
  onSaveDraft: () => void;
}) {
  const ui = useVisibleCopy();

  if (!detail || detail.loading) return <ResourceNotice loading notices={[]} />;
  if (kind === 'runtime') return <MCPRuntimeEvidencePanel model={detail.model} />;
  if (kind === 'policy' && detail.model.mode !== 'proxy') {
    return <EmptyState title="Proxy policy is unavailable for Native MCP." description="Use Edit connection for transport, command, URL, args, and secret refs." />;
  }
  return (
    <div className="mcp-drawer-stack">
      <section className={`panel mcp-mode-panel ${detail.model.mode === 'proxy' ? 'mcp-mode-proxy' : 'mcp-mode-native'}`}>
        <div className="panel-header"><div><h2>{ui(kind === 'policy' ? 'Proxy prompt / permission / audit policy' : 'MCP edit drawer')}</h2><p>{server?.usedByLabel ? ui(`Used by: ${server.usedByLabel}`) : ui('Usage is read-only and shown after live Persona snapshots reference this MCP.')}</p></div><span className="badge">{detail.model.modeLabel}</span></div>
        <PropertyTable rows={detail.model.summaryRows} />
      </section>
      {kind === 'policy' ? (
        <>
          <section className="panel"><div className="panel-header"><div><h2>{ui('Current Proxy policy summary')}</h2><p>{ui('Prompt injection, permission, audit policy, credential visibility, and schema visibility.')}</p></div></div><PropertyTable rows={detail.model.proxyFields} /></section>
          <section className="panel"><div className="panel-header"><div><h2>{ui('Current proxy rules')}</h2><p>{ui('Prompt injection and permission/audit details are on demand.')}</p></div></div><MCPProxyRuleTable rules={detail.model.proxyRules} /></section>
          <MCPDraftEditorPanel model={detail.model} draftForm={draftForm} dirty={dirty} saving={saving} onDraftFormChange={onDraftFormChange} onDiscard={onDiscard} onDraftJson={onDraftJson} onSaveDraft={onSaveDraft} focus="proxy" />
        </>
      ) : (
        <>
          <section className="panel"><div className="panel-header"><div><h2>{ui(detail.model.mode === 'proxy' ? 'Proxy MCP policy model' : 'Native MCP connection model')}</h2><p>{ui(detail.model.mode === 'proxy' ? 'MCP-owned prompt, permission, audit, and visibility controls.' : 'MCP-owned transport, command, URL, args, runtime name, and secret refs.')}</p></div></div><PropertyTable rows={detail.model.mode === 'proxy' ? detail.model.proxyFields : detail.model.nativeFields} /></section>
          <MCPDraftEditorPanel model={detail.model} draftForm={draftForm} dirty={dirty} saving={saving} onDraftFormChange={onDraftFormChange} onDiscard={onDiscard} onDraftJson={onDraftJson} onSaveDraft={onSaveDraft} focus={detail.model.mode} />
        </>
      )}
      <section className="panel"><div className="panel-header"><div><h2>{ui('Draft JSON / diff entry')}</h2><p>{ui('Full draft JSON opens from the editor action; it is not the primary editing path.')}</p></div><button className="btn btn-subtle" type="button" onClick={onDraftJson}>{ui('Open Draft JSON + diff')}</button></div></section>
      {detail.actionMessage ? <div className="notice"><strong>{ui('Result')}</strong> {ui(detail.actionMessage)}</div> : null}
    </div>
  );
}

function MCPLifecyclePanel({ model }: { model: MCPServerDetailModel }) {
  const ui = useVisibleCopy();
  const [selectedAction, setSelectedAction] = useState('validate');
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const mcpName = model.summaryRows.find(([label]) => label === 'Name')?.[1] || model.header.title;
  const destructive = selectedAction === 'archive' || selectedAction === 'delete' || selectedAction === 'rollback';
  const confirmationTarget = selectedAction === 'delete' ? `delete ${mcpName}` : `${selectedAction} ${mcpName}`;
  const previewRows: Array<[string, string]> = [
    ['Action', mcpLifecycleActionOptions.find(option => option.value === selectedAction)?.label ?? selectedAction],
    ['Target MCP', mcpName],
    ['Lifecycle contract', 'Separate from Save draft; backend MCP lifecycle preview/run endpoints are not exposed yet'],
    ['Destructive guard', destructive ? `Type "${confirmationTarget}" before this action can run once backend support exists` : 'Preview-only confirmation is sufficient for non-destructive actions'],
    ['Persona binding', 'Not available from MCP pages'],
  ];
  return (
    <div className="mcp-drawer-stack">
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('MCP lifecycle controls')}</h2><p>{ui('Lifecycle actions are intentionally separate from structured draft editing and remain preview-gated.')}</p></div></div>
        <ConfigSegmentedControl
          label="MCP lifecycle action"
          value={selectedAction}
          onChange={setSelectedAction}
          options={mcpLifecycleActionOptions}
        />
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Lifecycle preview')}</h2><p>{ui('Destructive actions require typed confirmation. Run support is disabled until backend MCP lifecycle contracts exist.')}</p></div></div>
        <PropertyTable rows={previewRows} />
        {destructive ? (
          <ConfigField label="Typed confirmation" helperText={`Required text: ${confirmationTarget}`}>
            <ConfigTextInput value={typedConfirmation} onChange={setTypedConfirmation} placeholder={confirmationTarget} />
          </ConfigField>
        ) : <ConfigHelperText>{ui('Non-destructive lifecycle actions still require preview before run.')}</ConfigHelperText>}
        <div className="action-row">
          <button className="btn btn-primary" type="button" disabled>{ui(destructive && typedConfirmation !== confirmationTarget ? 'Type confirmation to enable when supported' : 'Run unavailable: backend contract gap')}</button>
        </div>
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Backend contract gaps')}</h2><p>{ui('These gaps are surfaced instead of folding lifecycle into raw JSON editing.')}</p></div></div>
        <ul>{model.contractGaps.map(gap => <li key={gap}>{ui(gap)}</li>)}</ul>
      </section>
    </div>
  );
}

export function MCPDraftEditorPanel({
  model,
  draftForm,
  dirty,
  saving,
  onDraftFormChange,
  onDiscard,
  onDraftJson,
  onSaveDraft,
  focus,
}: {
  model: MCPServerDetailModel;
  draftForm: MCPDraftFormState;
  dirty: boolean;
  saving: boolean;
  onDraftFormChange: (value: MCPDraftFormState) => void;
  onDiscard: () => void;
  onDraftJson: () => void;
  onSaveDraft: () => void;
  focus?: 'native' | 'proxy';
}) {
  const ui = useVisibleCopy();
  const patchDraft = (patch: Partial<MCPDraftFormState>) => onDraftFormChange({ ...draftForm, ...patch });
  const updateProxyRule = (index: number, patch: Partial<MCPDefinitionDraftSpec['proxy_rules'][number]>) => {
    const rules = draftForm.proxy_rules.map((rule, ruleIndex) => ruleIndex === index ? { ...rule, ...patch } : rule);
    patchDraft({ proxy_rules: rules });
  };
  const statusValue = draftForm.status || model.artifactStatus || 'unknown';

  return (
    <ConfigEditorShell
      title={focus === 'proxy' ? 'Structured Proxy MCP configuration' : 'Structured Native MCP configuration'}
      description="Edit MCP-owned transport, args, env placeholders, secret refs, proxy flags, prompt injection, permission, and audit controls. Persona binding remains read-only outside MCP pages."
      source="draft"
      dirty={dirty}
      saving={saving}
      labels={{ draftJson: 'Draft JSON + diff', unsaved: 'Unsaved MCP draft' }}
      onSaveDraft={onSaveDraft}
      onDiscardChanges={onDiscard}
      onOpenDraftJson={onDraftJson}
    >
      <ConfigEditorSection title="Native MCP connection" description="Transport, command/URL, args, env, secret refs, and current status are prefilled from the current draft or published config.">
        <div className="content-grid">
          <ConfigCard title="Transport and endpoint" description="Use command for stdio MCPs and URL for HTTP/SSE MCPs; leave unrelated fields blank.">
            <ConfigField label="Transport" required>
              <ConfigSegmentedControl
                label="MCP transport"
                value={draftForm.type || 'stdio'}
                onChange={type => patchDraft({ type })}
                options={mcpTransportOptions}
              />
            </ConfigField>
            <ConfigField id="mcp-command" label="Command">
              <ConfigTextInput id="mcp-command" value={draftForm.command} onChange={command => patchDraft({ command })} placeholder="npx or server binary" />
            </ConfigField>
            <ConfigField id="mcp-url" label="URL">
              <ConfigTextInput id="mcp-url" type="url" value={draftForm.url} onChange={url => patchDraft({ url })} placeholder="https://example.com/mcp" />
            </ConfigField>
            <ConfigField id="mcp-runtime-name" label="Runtime name" helperText="Optional canonical runtime/display name; preset installs use this to keep scoped artifact names stable.">
              <ConfigTextInput id="mcp-runtime-name" value={draftForm.runtime_name ?? ''} onChange={runtimeName => patchDraft({ runtime_name: runtimeName || undefined })} placeholder="github-mcp" />
            </ConfigField>
            <ConfigField label="Artifact status" helperText="Status is shown as a structured read-only control. Lifecycle actions stay separate from Save draft.">
              <ConfigSegmentedControl
                label="MCP artifact status"
                value={statusValue}
                onChange={() => undefined}
                options={withCurrentMCPStatusOption(statusValue).map(option => ({ ...option, disabled: true }))}
              />
            </ConfigField>
          </ConfigCard>
          <ConfigCard title="Args and env" description="Args are repeatable structured rows. Env values are placeholders only; secret-like values are redacted on save, and credentials belong in secret refs.">
            <ConfigField label="Args">
              <SkillStringRows rows={draftForm.argRows} onChange={argRows => patchDraft({ argRows })} addLabel="Add arg" itemLabel="Arg" placeholder="--flag" />
            </ConfigField>
            <ConfigField label="Env placeholders" helperText="Use non-secret environment placeholders here; put credentials in secret refs below.">
              <ConfigKeyValueEditor entries={draftForm.envRows} onChange={envRows => patchDraft({ envRows })} addLabel="Add env" keyLabel="Env name" valueLabel="Value placeholder" />
            </ConfigField>
          </ConfigCard>
        </div>
      </ConfigEditorSection>

      <ConfigEditorSection title="Secret refs" description="Secret refs store registry keys and targets only. Console never renders or accepts secret values.">
        <ConfigRepeatableRows
          items={draftForm.secret_refs}
          onChange={secret_refs => patchDraft({ secret_refs })}
          createItem={() => ({ key: '', target: '', required: true, visibility: draftForm.proxy ? 'proxy_only' : 'compat' })}
          addLabel="Add secret ref"
          getItemKey={(ref, index) => `${ref.key}:${ref.target}:${index}`}
          renderItem={({ item, index, updateItem, removeItem }) => (
            <div className="form-grid nested-form-grid">
              <ConfigField label="Registry key">
                <ConfigTextInput value={item.key} onChange={key => updateItem({ ...item, key })} placeholder="github.token.prod" />
              </ConfigField>
              <ConfigField label="Target">
                <ConfigTextInput value={item.target} onChange={target => updateItem({ ...item, target })} placeholder="header.Authorization or env.GITHUB_TOKEN" />
              </ConfigField>
              <ConfigField label="Visibility">
                <ConfigSelect value={item.visibility} onChange={visibility => updateItem({ ...item, visibility })} options={mcpCredentialVisibilityOptions} />
              </ConfigField>
              <ConfigField label="Required">
                <ConfigToggle checked={item.required !== false} onChange={required => updateItem({ ...item, required })} label={`Secret ref ${index + 1} required`} onLabel="Required" offLabel="Optional" />
              </ConfigField>
              <div className="action-row form-span"><button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove secret ref')}</button></div>
            </div>
          )}
        />
        {!draftForm.secret_refs.length ? <ConfigHelperText>No secret refs configured. Add references instead of raw headers or env credentials.</ConfigHelperText> : null}
      </ConfigEditorSection>

      <ConfigEditorSection title="Proxy prompt, permission, audit, and flags" description="Proxy MCP controls are structured even when proxy=false, so converting a Native MCP to cc-connect Proxy MCP does not require raw JSON.">
        <div className="content-grid">
          <ConfigCard title="Proxy flags" description="These flags define whether cc-connect wraps the MCP and how credentials are exposed.">
            <ConfigField label="Proxy enabled">
              <ConfigToggle
                checked={draftForm.proxy}
                onChange={proxy => patchDraft({ proxy, credential_visibility: proxy ? 'proxy_only' : draftForm.credential_visibility })}
                label="Proxy enabled"
                onLabel="cc-connect Proxy MCP"
                offLabel="Native MCP"
              />
            </ConfigField>
            <ConfigField label="Credential visibility">
              <ConfigSelect value={draftForm.credential_visibility} onChange={credential_visibility => patchDraft({ credential_visibility })} options={mcpCredentialVisibilityOptions} />
            </ConfigField>
            <ConfigHelperText>Permission placeholders and audit settings apply when proxy=true; usage evidence remains read-only.</ConfigHelperText>
          </ConfigCard>
          <ConfigCard title="Current policy summary" description="Read-only summary from the loaded draft/current spec.">
            <PropertyTable rows={model.proxyFields} />
          </ConfigCard>
        </div>
        <ConfigCard title="Proxy rules" description="Each rule can inject prompt guidance, block a tool with a placeholder message, enable audit metadata/params, and provide structured default params.">
          <ConfigRepeatableRows
            items={draftForm.proxy_rules}
            onChange={proxy_rules => patchDraft({ proxy_rules })}
            createItem={() => ({ tool: '*', audit: true })}
            addLabel="Add proxy rule"
            getItemKey={(rule, index) => `${rule.tool}:${index}`}
            renderItem={({ item, index, updateItem, removeItem }) => (
              <div className="form-grid nested-form-grid">
                <ConfigField label="Tool">
                  <ConfigTextInput value={item.tool} onChange={tool => updateItem({ ...item, tool })} placeholder="* or tool name" />
                </ConfigField>
                <ConfigField label="Permission">
                  <ConfigSegmentedControl
                    label={`Proxy permission ${index + 1}`}
                    value={item.blocked ? 'blocked' : 'allow'}
                    onChange={permission => updateItem({ ...item, blocked: permission === 'blocked' })}
                    options={mcpPermissionOptions}
                  />
                </ConfigField>
                <ConfigField label="Prompt injection" helperText="Appended to the MCP tool description; keep values non-secret.">
                  <ConfigTextarea rows={4} value={item.description_append ?? ''} onChange={description_append => updateItem({ ...item, description_append: description_append || undefined })} placeholder="Use repo-safe operations and explain side effects." />
                </ConfigField>
                <ConfigField label="Permission placeholder / blocked message">
                  <ConfigTextInput value={item.blocked_message ?? ''} onChange={blocked_message => updateItem({ ...item, blocked_message: blocked_message || undefined })} placeholder="Request denied by proxy policy." />
                </ConfigField>
                <ConfigField label="Audit metadata">
                  <ConfigToggle checked={item.audit === true} onChange={audit => updateItem({ ...item, audit })} label={`Audit proxy rule ${index + 1}`} onLabel="Audit on" offLabel="Audit off" />
                </ConfigField>
                <ConfigField label="Audit params">
                  <ConfigToggle checked={item.audit_params === true} onChange={audit_params => updateItem({ ...item, audit_params })} label={`Audit params ${index + 1}`} onLabel="Params on" offLabel="Params off" />
                </ConfigField>
                <div className="form-span">
                  <ConfigField label="Default params">
                    <ConfigKeyValueEditor entries={mcpKeyValueEntriesFromRecord(item.default_params)} onChange={entries => updateProxyRule(index, { default_params: mcpRecordFromConfigEntries(entries) })} addLabel="Add default param" keyLabel="Param" valueLabel="JSON/value" />
                  </ConfigField>
                </div>
                <div className="action-row form-span"><button className="btn btn-compact btn-subtle" type="button" onClick={removeItem}>{ui('Remove proxy rule')}</button></div>
              </div>
            )}
          />
          {!draftForm.proxy_rules.length ? <ConfigHelperText>No proxy rules configured. Add rules for prompt injection, permissions, and audit policy.</ConfigHelperText> : null}
        </ConfigCard>
      </ConfigEditorSection>

      <ConfigEditorSection title="Backend contract gaps" description="Unsupported lifecycle actions stay separate and visible; they are not hidden inside the editor.">
        <ul>{model.contractGaps.map(gap => <li key={gap}>{gap}</li>)}</ul>
      </ConfigEditorSection>
    </ConfigEditorShell>
  );
}

function MCPDetailOverview({
  model,
  draftForm,
  dirty,
  saving,
  onDraftFormChange,
  onDiscard,
  onDraftJson,
  onSaveDraft,
}: {
  model: MCPServerDetailModel;
  draftForm: MCPDraftFormState;
  dirty: boolean;
  saving: boolean;
  onDraftFormChange: (value: MCPDraftFormState) => void;
  onDiscard: () => void;
  onDraftJson: () => void;
  onSaveDraft: () => void;
}) {
  const ui = useVisibleCopy();
  const modeRows = model.mode === 'proxy' ? model.proxyFields : model.nativeFields;
  return (
    <div className="page-stack">
      <div className="content-grid">
        <section className={`panel mcp-mode-panel ${model.mode === 'proxy' ? 'mcp-mode-proxy' : 'mcp-mode-native'}`}>
          <div className="panel-header"><div><h2>{ui('Definition summary')}</h2><p>{ui(model.readonlyState)}</p></div><span className="badge">{ui(model.modeLabel)}</span></div>
          <PropertyTable rows={model.summaryRows} />
        </section>
        <section className="panel">
          <div className="panel-header"><div><h2>{ui(model.mode === 'proxy' ? 'Proxy MCP policy model' : 'Native MCP connection model')}</h2><p>{ui(model.mode === 'proxy' ? 'MCP-owned prompt, permission, audit, and visibility controls.' : 'MCP-owned connection, transport, command, URL, args, env, and secret refs.')}</p></div></div>
          <PropertyTable rows={modeRows} />
        </section>
      </div>
      <div className="content-grid">
        <section className="panel">
          <div className="panel-header"><div><h2>{ui('Secret refs')}</h2><p>{ui('Registry references only; token values are never shown or accepted.')}</p></div></div>
          <MCPSecretRefTable refs={model.secretRefs} />
        </section>
        <section className="panel">
          <div className="panel-header"><div><h2>{ui('Proxy rules')}</h2><p>{ui('Read-only policy evidence from the current MCP spec.')}</p></div></div>
          <MCPProxyRuleTable rules={model.proxyRules} />
        </section>
      </div>
      <MCPDraftEditorPanel
        model={model}
        draftForm={draftForm}
        dirty={dirty}
        saving={saving}
        onDraftFormChange={onDraftFormChange}
        onDiscard={onDiscard}
        onDraftJson={onDraftJson}
        onSaveDraft={onSaveDraft}
        focus={model.mode}
      />
    </div>
  );
}

function MCPSecretRefTable({ refs }: { refs: MCPServerDetailModel['secretRefs'] }) {
  const ui = useVisibleCopy();

  if (!refs.length) return <EmptyState title="No secret refs configured." description="Use secret_refs metadata instead of raw headers/env values." />;
  return (
    <table className="data-table mcp-compact-table">
      <thead><tr><th>{ui('Reference')}</th><th>{ui('Target')}</th><th>{ui('Required')}</th><th>{ui('Visibility')}</th></tr></thead>
      <tbody>{refs.map((ref, index) => (
        <tr key={`${ref.key}:${ref.target}:${index}`}>
          <td data-label={ui('Reference')}>{ui(`Configured ref #${index + 1}`)}<br /><small>{ui('key hidden')}</small></td>
          <td data-label={ui('Target')}>{ref.target}</td>
          <td data-label={ui('Required')}>{ui(ref.required ? 'Yes' : 'No')}</td>
          <td data-label={ui('Visibility')}>{ref.visibility}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function MCPProxyRuleTable({ rules }: { rules: MCPServerDetailModel['proxyRules'] }) {
  const ui = useVisibleCopy();

  if (!rules.length) return <EmptyState title="No proxy rules configured." description="Proxy MCPs can add prompt description_append, permission blocks, audit flags, and default params." />;
  return (
    <table className="data-table mcp-compact-table">
      <thead><tr><th>{ui('Tool/schema visibility')}</th><th>{ui('Prompt injection')}</th><th>{ui('Permission')}</th><th>{ui('Audit')}</th></tr></thead>
      <tbody>{rules.map((rule, index) => (
        <tr key={`${rule.tool}:${index}`}>
          <td data-label={ui('Tool/schema visibility')}><strong>{rule.tool}</strong><br /><small>{rule.defaultParams}</small></td>
          <td data-label={ui('Prompt injection')}>{rule.descriptionAppend}</td>
          <td data-label={ui('Permission')}>{rule.permission}<br /><small>{rule.blockedMessage}</small></td>
          <td data-label={ui('Audit')}>{rule.audit}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function MCPUsagePanel({ model }: { model: MCPServerDetailModel }) {
  const ui = useVisibleCopy();

  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{ui('Read-only usage / consumers')}</h2><p>{ui('Persona binding stays in Persona config; this table only links to owning config and runtime evidence.')}</p></div></div>
      {model.consumers.length ? (
        <table className="data-table mcp-compact-table">
          <thead><tr><th>{ui('Consumer')}</th><th>{ui('Source')}</th><th>{ui('Referenced version')}</th><th>{ui('Runtime state')}</th><th>{ui('Navigate')}</th></tr></thead>
          <tbody>{model.consumers.map(consumer => (
            <tr key={consumer.key}>
              <td data-label={ui('Consumer')}><strong>{consumer.personaName}</strong><br /><small>{consumer.personaId}</small></td>
              <td data-label={ui('Source')}>{consumer.source}</td>
              <td data-label={ui('Referenced version')}>{consumer.referencedVersion}</td>
              <td data-label={ui('Runtime state')}>{consumer.runtimeState}</td>
              <td data-label={ui('Navigate')}><a href={consumer.configHref}>{ui('Persona config')}</a><br /><a href={consumer.runtimeHref}>{ui('Runtime evidence')}</a></td>
            </tr>
          ))}</tbody>
        </table>
      ) : <EmptyState title="No consumers found." description="Backend has no MCP consumers endpoint; this view aggregates live Persona detail snapshots only." />}
    </section>
  );
}

function MCPRuntimeEvidencePanel({ model }: { model: MCPServerDetailModel }) {
  const ui = useVisibleCopy();

  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Proxy runtime evidence')}</h2><p>{ui('Active proxy state, config precedence, last-call availability, audit health, and schema visibility.')}</p></div><span className="badge">{ui(`audit rows ${model.auditEventsCount}`)}</span></div>
        {model.runtimeEvidence.length ? (
          <table className="data-table mcp-runtime-table">
            <thead><tr><th>{ui('Persona/server')}</th><th>{ui('Active proxy state')}</th><th>{ui('Shadowing / precedence')}</th><th>{ui('Last call')}</th><th>{ui('Audit health')}</th><th>{ui('Tool/schema visibility')}</th></tr></thead>
            <tbody>{model.runtimeEvidence.map(row => (
              <tr key={row.key}>
                <td data-label={ui('Persona/server')}><strong>{row.personaName}</strong><br /><a href={row.runtimeHref}>{row.serverName}</a><br /><small>{row.mode === 'proxy' ? 'Proxy MCP' : 'Native MCP'}</small></td>
                <td data-label={ui('Active proxy state')}>{row.activeProxyState}</td>
                <td data-label={ui('Shadowing / precedence')}>{row.configPrecedence}<br /><small>{row.sourceOrigin}</small></td>
                <td data-label={ui('Last call')}>{row.lastCall}</td>
                <td data-label={ui('Audit health')}>{row.auditHealth}</td>
                <td data-label={ui('Tool/schema visibility')}>{row.toolVisibility}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <EmptyState title="No runtime evidence found." description="Runtime evidence is available only when Persona detail snapshots expose this MCP by artifact_id or name." />}
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Tool definitions')}</h2><p>{ui('Partial/read-only state.')}</p></div></div>
        <div className="notice notice-warning"><strong>{ui('Partial data')}</strong><p>{model.toolDefinitionState}</p></div>
      </section>
    </div>
  );
}

function MCPDraftJsonDrawerContent({ model }: { model: MCPServerDetailModel }) {
  const ui = useVisibleCopy();

  return (
    <div className="record-list">
      <p>{ui('Redacted draft-vs-published diff. This is the only draft JSON drawer; View Config JSON remains the single full JSON tab.')}</p>
      <div><h3>{ui('Draft')}</h3><pre className="json-preview">{model.draftJsonDrawer.draftJson}</pre></div>
      <div><h3>{ui('Published')}</h3><pre className="json-preview">{model.draftJsonDrawer.publishedJson}</pre></div>
      <div><h3>{ui('Diff')}</h3><pre className="json-preview">{model.draftJsonDrawer.diffText}</pre></div>
    </div>
  );
}

function MCPServerTable({ servers, evidenceLabel, runtimeLabel, onAction }: { servers: MCPServersModel['servers']; evidenceLabel: string; runtimeLabel: string; onAction: (drawer: MCPListDrawer) => void }) {
  const { t } = useI18n();
  const ui = useVisibleCopy();

  return (
    <table className="data-table mcp-definition-table">
      <thead><tr><th>{ui('Name')}</th><th>{ui('Mode')}</th><th>{ui('Status')}</th><th>{evidenceLabel}</th><th>{runtimeLabel}</th><th>{ui('Credential policy')}</th><th>{ui('Used by')}</th><th>{ui('Row actions')}</th></tr></thead>
      <tbody>{servers.map(server => (
        <tr key={server.id}>
          <td data-label={ui('Name')}><strong>{server.displayName}</strong><br /><small>{server.name}</small></td>
          <td data-label={ui('Mode')}><span className={`pill mcp-mode-pill ${server.mode === 'proxy' ? 'pill-green' : 'pill-blue'}`}>{server.modeLabel}</span><br /><small>{server.configModel}</small></td>
          <td data-label={ui('Status')}>{server.status}<br /><small>{server.type}{server.proxy ? ' + proxy' : ''}</small></td>
          <td data-label={evidenceLabel}>{server.evidenceState}</td>
          <td data-label={runtimeLabel}>{server.runtimeExposure}</td>
          <td data-label={ui('Credential policy')}>{server.credentialPolicy}</td>
          <td data-label={ui('Used by')}>{server.usedByLabel}</td>
          <td data-label={ui('Row actions')}><div className="action-row row-actions mcp-row-actions"><button className="btn btn-compact btn-subtle" type="button" onClick={() => onAction({ kind: 'edit', serverId: server.id })}>{server.mode === 'proxy' ? t(rmKey('actions.editConfig')) : t(rmKey('actions.editConnection'))}</button>{server.mode === 'proxy' ? <button className="btn btn-compact btn-subtle" type="button" onClick={() => onAction({ kind: 'policy', serverId: server.id })}>{t(rmKey('actions.promptPolicy'))}</button> : null}<button className="btn btn-compact btn-subtle" type="button" onClick={() => onAction({ kind: 'runtime', serverId: server.id })}>{t(rmKey('actions.runtimeEvidence'))}</button><a className="btn btn-compact btn-subtle" href={server.detailHref}>{t(rmKey('actions.detailTabs'))}</a></div></td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function LoadedSkillCard({ row }: { row: LoadedSkillRow }) {
  const { t } = useI18n();
  const ui = useVisibleCopy();

  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{ui('Runtime evidence')}</h2><p>{row.runtimeEvidence}</p></div></div>
      <PropertyTable rows={[
        ['Persona', row.personaName],
        ['Skill', row.skillName],
        ['Display name', row.displayName],
        ['Managed', row.managed ? 'Yes' : 'No'],
        ['Enabled', row.enabled ? 'Yes' : 'No'],
        [t(rmKey('labels.evidenceState')), row.evidenceState],
        ['Scope', row.scope],
        ['Source origin', row.sourceOrigin],
        ['Artifact ID', row.artifactId || 'Unavailable'],
      ]} />
    </section>
  );
}

function ToolProxyCard({ row }: { row: Parameters<typeof ToolProxyTables>[0]['model']['proxyRows'][number] }) {
  const ui = useVisibleCopy();

  return (
    <section className="panel">
      <div className="panel-header"><div><h2>{ui('Proxy evidence')}</h2><p>{row.runtimeEvidence}</p></div></div>
      <PropertyTable rows={[
        ['Persona', row.personaName],
        ['Server', row.serverName],
        ['Transport', row.type],
        ['Proxy enabled', row.proxy ? 'Yes' : 'No'],
        ['Local proxy', row.localProxyAddr || 'Unavailable'],
        ['Credential visibility', row.credentialVisibility],
        ['Source origin', row.sourceOrigin],
        ['Proxy rule count', String(row.proxyRulesCount)],
        ['Artifact ID', row.artifactId || 'Unavailable'],
      ]} />
    </section>
  );
}

export function ToolProxyTables({ model }: { model: ToolProxyModel }) {
  const { t } = useI18n();
  const ui = useVisibleCopy();

  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Live proxy rows')}</h2><p>{ui('Runtime MCP proxy state from persona detail snapshots.')}</p></div></div>
        {model.emptyState ? <EmptyState {...model.emptyState} /> : (
          <table className="data-table">
            <thead><tr><th>{ui('Persona')}</th><th>{ui('Server')}</th><th>{t(rmKey('table.evidence'))}</th><th>{ui('Proxy')}</th><th>{ui('Credential policy')}</th><th>{t(rmKey('labels.actions'))}</th></tr></thead>
            <tbody>{model.proxyRows.map(row => (
              <tr key={`${row.personaName}:${row.serverName}`}>
                <td>{row.personaName}</td>
                <td><strong>{row.serverName}</strong><br /><small>{row.type}</small></td>
                <td>{row.evidenceState}<br /><small>{row.runtimeEvidence}</small></td>
                {/* The local proxy address is this process, not the agent. Showing it
                    for a server the adapter cannot be handed is what made this page
                    read as healthy for an agent with no tools. */}
                <td>{row.deliveredToAgent === false ? ui('Not delivered to agent') : ui(row.proxy ? row.localProxyAddr || 'Enabled' : 'Disabled')}</td>
                <td>{row.credentialVisibility}</td>
                <td><div className="action-row row-actions"><a className="btn btn-compact btn-subtle" href={row.detailHref}>{t(rmKey('actions.openDetail'))}</a></div></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </section>
      <section className="panel">
        <div className="panel-header"><div><h2>{ui('Shadowed managed MCPs')}</h2><p>{ui('Managed servers dropped by config-wins merge semantics.')}</p></div></div>
        <RecordList records={model.shadowedRows} emptyTitle="No shadowed managed MCPs returned." />
      </section>
    </div>
  );
}

function PresetTable({ presets, onImport }: { presets: MCPPresetsModel['presets']; onImport: (presetName: string) => void }) {
  const { t } = useI18n();
  const ui = useVisibleCopy();

  return (
    <table className="data-table">
      <thead><tr><th>{ui('Name')}</th><th>{ui('Type')}</th><th>{ui('Credential requirements')}</th><th>{ui('Action state')}</th><th>{ui('Actions')}</th></tr></thead>
      <tbody>{presets.map(preset => (
        <tr key={preset.name}>
          <td><strong>{preset.displayName}</strong><br /><small>{preset.description || preset.name}</small></td>
          <td>{preset.type}{preset.proxy ? ' + proxy' : ''}</td>
          <td>{preset.credentialPolicy}</td>
          <td>{preset.evidenceState}<br /><small>{preset.actionState}</small></td>
          <td><div className="action-row row-actions mcp-row-actions"><button className="btn btn-compact btn-subtle" type="button" onClick={() => onImport(preset.name)}>{t(rmKey('actions.importDrawer'))}</button><a className="btn btn-compact btn-subtle" href={preset.detailHref}>{t(rmKey('actions.detail'))}</a></div></td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function JsonBlock({ value, empty, redactPromptContent = true }: { value: unknown; empty: string; redactPromptContent?: boolean }) {
  if (value === undefined || value === null || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0)) {
    return <EmptyState title="No JSON evidence" description={empty} />;
  }
  return <pre className="json-preview">{redactedJson(value, { redactPromptContent })}</pre>;
}

function MarkdownPreview({ markdown }: { markdown: string }) {
  const ui = useVisibleCopy();
  return <div className="markdown-preview">{markdownToNodes(markdown, ui)}</div>;
}

function markdownToNodes(markdown: string, ui: (value: string) => string) {
  const nodes: ReactNode[] = [];
  let listItems: string[] = [];
  const flushList = () => {
    if (!listItems.length) return;
    const items = listItems;
    listItems = [];
    nodes.push(<ul key={`ul-${nodes.length}`}>{items.map(item => <li key={item}>{renderInlineMarkdown(item)}</li>)}</ul>);
  };
  markdown.split('\n').forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listItems.push(trimmed.slice(2));
      return;
    }
    flushList();
    if (trimmed.startsWith('### ')) nodes.push(<h3 key={index}>{renderInlineMarkdown(trimmed.slice(4))}</h3>);
    else if (trimmed.startsWith('## ')) nodes.push(<h2 key={index}>{renderInlineMarkdown(trimmed.slice(3))}</h2>);
    else if (trimmed.startsWith('# ')) nodes.push(<h1 key={index}>{renderInlineMarkdown(trimmed.slice(2))}</h1>);
    else nodes.push(<p key={index}>{renderInlineMarkdown(trimmed)}</p>);
  });
  flushList();
  return nodes.length ? nodes : <p>{ui('No Markdown content.')}</p>;
}

function renderInlineMarkdown(value: string) {
  const parts = value.split(/(`[^`]+`)/g);
  return parts.map((part, index) => part.startsWith('`') && part.endsWith('`')
    ? <code key={index}>{part.slice(1, -1)}</code>
    : part);
}

function RecordList({ records, emptyTitle }: { records: Record<string, unknown>[]; emptyTitle: string }) {
  if (!records.length) return <EmptyState title={emptyTitle} description="No live management API rows are available for this section." />;
  return (
    <div className="record-list">
      {records.map((record, index) => (
        <article className="record-card" key={index}>
          <pre>{redactedJson(record, { redactPromptContent: true })}</pre>
        </article>
      ))}
    </div>
  );
}

function ResourceNotice({ loading, notices }: { loading: boolean; notices: string[] }) {
  const ui = useVisibleCopy();

  if (loading) return <div className="notice"><strong>{ui('Loading live management API data…')}</strong></div>;
  if (!notices.length) return null;
  return (
    <div className="notice notice-warning">
      <strong>{ui('Partial/read-only data')}</strong>
      <ul>{notices.map(notice => <li key={notice}>{ui(notice)}</li>)}</ul>
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  const ui = useVisibleCopy();
  return <div className="empty-state"><strong>{ui(title)}</strong><p>{ui(description)}</p></div>;
}

async function capture(label: string, promise: Promise<unknown>, errors: Record<string, string>) {
  try {
    return await promise;
  } catch (error) {
    errors[label] = error instanceof Error ? error.message : String(error);
    return undefined;
  }
}

function extractPersonaNames(value: unknown) {
  return getArray(asRecord(value).personas).map(item => text(asRecord(item).name)).filter(Boolean);
}

function normalizeTab(value: string | null | undefined): ResourceTab {
  return value === 'json' ? 'json' : 'overview';
}

function normalizeMCPDetailTab(value: string | null | undefined): MCPDetailTab {
  return value === 'usage' || value === 'runtime' || value === 'json' ? value : 'overview';
}

const mcpTransportOptions = [
  { value: 'stdio', label: 'stdio' },
  { value: 'http', label: 'http' },
  { value: 'sse', label: 'sse' },
];

const mcpCredentialVisibilityOptions = [
  { value: 'compat', label: 'compat' },
  { value: 'proxy_only', label: 'proxy_only' },
];

const mcpPermissionOptions = [
  { value: 'allow', label: 'Allow' },
  { value: 'blocked', label: 'Blocked' },
];

const mcpStatusOptions = [
  { value: 'draft', label: 'draft' },
  { value: 'active', label: 'active' },
  { value: 'disabled', label: 'disabled' },
  { value: 'archived', label: 'archived' },
  { value: 'unknown', label: 'unknown' },
];

const mcpLifecycleActionOptions = [
  { value: 'validate', label: 'Validate draft' },
  { value: 'create_version', label: 'Create version' },
  { value: 'publish', label: 'Publish' },
  { value: 'rollback', label: 'Rollback' },
  { value: 'archive', label: 'Archive' },
  { value: 'delete', label: 'Delete' },
];

function draftFormFromSpec(spec: MCPDefinitionDraftSpec, status = ''): MCPDraftFormState {
  return {
    ...spec,
    type: spec.type || 'stdio',
    command: spec.command ?? '',
    url: spec.url ?? '',
    proxy: spec.proxy === true,
    credential_visibility: spec.credential_visibility || (spec.proxy ? 'proxy_only' : 'compat'),
    secret_refs: (spec.secret_refs ?? []).map(ref => ({ ...ref, required: ref.required !== false, visibility: ref.visibility || (spec.proxy ? 'proxy_only' : 'compat') })),
    proxy_rules: (spec.proxy_rules ?? []).map(rule => ({ ...rule, tool: rule.tool || '*' })),
    argRows: (spec.args ?? []).map(value => skillStringRow(value)),
    envRows: mcpKeyValueEntriesFromRecord(spec.env),
    status,
  };
}

function draftSpecFromForm(form: MCPDraftFormState): MCPDefinitionDraftSpec {
  const spec: MCPDefinitionDraftSpec = {
    type: form.type || 'stdio',
    command: form.command.trim(),
    args: form.argRows.map(item => item.value.trim()).filter(Boolean),
    url: form.url.trim(),
    proxy: form.proxy,
    credential_visibility: form.credential_visibility || (form.proxy ? 'proxy_only' : 'compat'),
    secret_refs: form.secret_refs.map(ref => ({
      key: ref.key.trim(),
      target: ref.target.trim(),
      required: ref.required !== false,
      visibility: ref.visibility || (form.proxy ? 'proxy_only' : 'compat'),
    })).filter(ref => ref.key || ref.target),
    proxy_rules: form.proxy_rules.map(rule => {
      const next: MCPDefinitionDraftSpec['proxy_rules'][number] = {
        tool: rule.tool.trim() || '*',
      };
      if (rule.description_append?.trim()) next.description_append = rule.description_append.trim();
      if (rule.blocked === true) next.blocked = true;
      if (rule.blocked_message?.trim()) next.blocked_message = rule.blocked_message.trim();
      if (rule.audit === true) next.audit = true;
      if (rule.audit_params === true) next.audit_params = true;
      const defaultParams = mcpCleanRecord(rule.default_params);
      if (Object.keys(defaultParams).length) next.default_params = defaultParams;
      return next;
    }).filter(rule => rule.tool),
  };
  const env = mcpRecordFromConfigEntries(form.envRows, { stringifyValues: true });
  if (Object.keys(env).length) spec.env = env as Record<string, string>;
  if (form.runtime_name?.trim()) spec.runtime_name = form.runtime_name.trim();
  return spec;
}

function mcpImportDraftState(presetName: string, projectId: string, secretBindings: Record<string, string>): MCPImportDraftState {
  return {
    presetName,
    projectId,
    secretBindings: Object.fromEntries(Object.entries(secretBindings).filter(([key, value]) => key.trim() || value.trim()).sort(([left], [right]) => left.localeCompare(right))),
  };
}

function withCurrentMCPStatusOption(value: string) {
  return mcpStatusOptions.some(option => option.value === value) ? mcpStatusOptions : [{ value, label: value }, ...mcpStatusOptions];
}

function mcpKeyValueEntriesFromRecord(record: unknown): ConfigKeyValueEntry[] {
  return Object.entries(asRecord(record)).map(([key, value], index) => ({
    id: `mcp-kv-${key || index}`,
    key,
    value: mcpEntryValue(key, value),
  }));
}

function mcpRecordFromConfigEntries(entries: ConfigKeyValueEntry[], options: { stringifyValues?: boolean } = {}): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  entries.forEach(entry => {
    const key = entry.key.trim();
    if (!key) return;
    if (isSensitiveKey(key) && entry.value === REDACTED_VALUE) return;
    const parsedValue = options.stringifyValues ? entry.value : parseMCPEntryValue(entry.value);
    record[key] = typeof parsedValue === 'string' ? redactSensitiveText(parsedValue) : parsedValue;
  });
  return record;
}

function mcpCleanRecord(record: unknown): Record<string, unknown> {
  return Object.fromEntries(Object.entries(asRecord(record)).filter(([key, value]) => key.trim() && value !== undefined && value !== ''));
}

function mcpEntryValue(key: string, value: unknown) {
  if (isSensitiveKey(key) && value !== undefined && value !== null && value !== '') return REDACTED_VALUE;
  if (typeof value === 'string') return redactSensitiveText(value);
  if (value === undefined || value === null) return '';
  return redactedJson(value, { redactPromptContent: true });
}

function parseMCPEntryValue(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function text(value: unknown) {
  return typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
